import { supabase } from './supabase'

// ============================================================================
// Waiver type registry — mirrors api/_lib/waivers-v1.js (browser-safe).
// Keep in sync: if you add a new type here, add it there too and bump the
// version tag. The full legal body text lives on the server only.
// ============================================================================
export const WAIVER_VERSION = 'v1-2026-04'

// Current Terms of Service version tag. Bump this whenever the ToS text
// changes — any prior client-level signature at a different version is
// treated as invalid for carry-forward and the client will need to re-sign.
// Must stay in sync with api/_lib/terms-v1.js TERMS_VERSION.
export const TERMS_VERSION = 'v1-2026-04'

// How long a client-level terms signature stays valid for carry-forward.
// After this window the client re-signs on their next estimate regardless
// of version match.
const TERMS_CARRY_FORWARD_DAYS = 365

// ============================================================================
// Phase kinds — registry of the allowed stages of a multi-day move. Each
// estimate's planned_phases array carries one entry per day/stage, and every
// move_jobs row that gets pushed to dispatch carries its kind + sequence.
//
// The CHECK constraint on move_jobs.phase_kind enforces the same list — see
// sql/020_phases.sql (original 5 kinds) and sql/024_phase_kinds_expanded.sql
// (folded in drive/return/hold from the old Trip Planner span tags).
//
// Ordering here drives the dropdown order in the Phase Planner. LOCAL jobs
// only use pack/move/trash_removal; BOL jobs use pack/load/drive/unload/
// return/hold. There's no enforcement of that split — a dispatcher is free
// to mix any kinds on any estimate.
// ============================================================================
// Phase-kind metadata & behavior moved to src/lib/phaseKinds.js
// (2026-08-26 combo refactor): PHASE_KINDS is now the 8 BASE segments
// only — combos ('load_drive', 'drive+unload+return', ...) are built by
// merging chips in the planner and validated via isValidKind, which
// accepts base kinds, the legacy combo names, and '+'-joined composites.
import { shortToken } from './publicLinks'
import { PHASE_KINDS as BASE_PHASE_KINDS, isValidKind, kindIsAnchor, kindIsPack, kindHasLabor, kindIsHoldOnly, kindSegments, ghostHoldFields, phaseLaborPricing, phasePackPricing } from './phaseKinds'
import { MOVE_MIRROR_CLIENT, MOVE_MIRROR_ESTIMATE, MOVE_DATE_FIELDS, MOVE_IDENTITY_FIELDS, estimateFieldsFromMove, stripKeys, addDaysYmd, daysBetween } from './moves'
export const PHASE_KINDS = BASE_PHASE_KINDS
export { kindSegments, kindId, isValidKind, isComboKind } from './phaseKinds'

// Helper: build a fresh planned-phase object with sensible defaults. Used
// for auto-seeding on estimate create and for the "Add Phase" button.
// Exported so UI components (PhasePlannerInline) can build phases locally
// before the estimate is saved, using the exact same shape the DB layer
// produces after a save.
//
// `duration_days` enables spanning phases (e.g. a 9-day HOLD) without
// forcing the dispatcher to add 9 separate phase rows. When a phase has
// duration_days > 1 and a date set, pushPhasesToDispatch expands it into
// N consecutive move_jobs rows. For dateless (unscheduled) phases the
// duration is still tracked but not materialized until a date is chosen.
export function makePhase({ kind = 'move', sequence = 1, date = null, time = null, duration_days = 1, crew_size = 0, trucks_needed = '', dispatcher_notes = '' } = {}) {
  if (!isValidKind(kind)) throw new Error(`Unknown phase kind: ${kind}`)
  return {
    kind,
    sequence,
    date: date || null,
    time: time || null,
    duration_days: Math.max(1, Number(duration_days) || 1),
    crew_size: Number(crew_size) || 0,
    trucks_needed: trucks_needed || '',
    dispatcher_notes: dispatcher_notes || '',
    // move_job_ids is the canonical list of dispatch rows this phase has
    // been materialized into — one entry per day for spanning phases.
    // move_job_id is kept for backward-compat and always mirrors [0].
    move_job_ids: [],
    move_job_id: null,
  }
}

// Helper: normalize an array of phases — re-sequences them 1..N in order,
// validates kinds, and guarantees every phase has the required shape.
// Exported for the same reason as makePhase.
export function normalizePhases(phases) {
  if (!Array.isArray(phases)) return []
  return phases
    .filter(p => p && isValidKind(p.kind))
    .map((p, idx) => {
      // Legacy phases only carried a single move_job_id. Promote it into
      // the new move_job_ids array so downstream code can assume array shape.
      const legacyIds = Array.isArray(p.move_job_ids)
        ? p.move_job_ids.filter(Boolean)
        : (p.move_job_id ? [p.move_job_id] : [])
      return {
        kind: p.kind,
        sequence: idx + 1,
        date: p.date || null,
        time: p.time || null,
        duration_days: Math.max(1, Number(p.duration_days) || 1),
        crew_size: Number(p.crew_size) || 0,
        trucks_needed: p.trucks_needed || '',
        dispatcher_notes: p.dispatcher_notes || '',
        move_job_ids: legacyIds,
        move_job_id: legacyIds[0] || null,
        // Per-day pricing for multi-day LOCAL estimates (2026-06-09).
        // Each working phase can carry its own hours range + hourly rate
        // (rate follows the day's crew size via the rate table). Null /
        // 0 = phase not individually priced (single-day estimates keep
        // the flat hours × rate model).
        est_min_hours: Number(p.est_min_hours) || 0,
        est_max_hours: Number(p.est_max_hours) || 0,
        hourly_rate: Number(p.hourly_rate) || 0,
        // Packing pricing for a pack+labor COMBO day (2026-09-10) — kept
        // apart from the labor fields above so merging a priced pack day
        // into a move day can't turn into "3 hrs × $125 labor". Pack-ONLY
        // days still carry packing pricing in est_*/hourly_rate; read
        // through phaseLaborPricing / phasePackPricing (phaseKinds.js).
        pack_min_hours: Number(p.pack_min_hours) || 0,
        pack_max_hours: Number(p.pack_max_hours) || 0,
        pack_rate: Number(p.pack_rate) || 0,
        // Leg-local stop (delivery / pickup / storage-out, 2026-09-18):
        // blank = the move's own addresses.
        from_address: p.from_address || null,
        to_address: p.to_address || null,
      }
    })
}
export const WAIVER_DEFS = [
  { key: 'appliance',     shortLabel: 'Appliance Handling',            summary: 'BBM not responsible for appliance disconnect/reconnect (water, gas, electric).' },
  { key: 'oversized',     shortLabel: 'Oversized / Restricted Access', summary: 'Item is oversized or access is restricted (stairs, halls, tight corners).' },
  { key: 'thin_tv',       shortLabel: 'Thin TV (no original box)',     summary: 'TV transported without its original manufacturer packaging.' },
  { key: 'pre_damaged',   shortLabel: 'Pre-Damaged / Aged Items',      summary: 'Items are aged, fragile, or already damaged.' },
  { key: 'wrap_decline',  shortLabel: 'Declined Furniture Wrapping',   summary: 'Client declined full-wrap and protection service.' },
]
const VALID_WAIVER_KEYS = new Set(WAIVER_DEFS.map(w => w.key))

// Shared HH:MM clock math for the per-crew daily hours feature (sql/057).
// Paid hours = (clock_out - clock_in) - total break minutes. Returns a
// finite non-negative number rounded to 2dp, or null when either clock
// side is missing/unparseable. Mirrored server-side in
// api/crew-portal-data.js (separate Vercel bundle, can't import this).
export function computeDayHours(clockIn, clockOut, breakMinutes = 0) {
  if (!clockIn || !clockOut) return null
  const [sh, sm] = String(clockIn).split(':').map(Number)
  const [eh, em] = String(clockOut).split(':').map(Number)
  if ([sh, sm, eh, em].some(Number.isNaN)) return null
  const totalMin = (eh * 60 + em) - (sh * 60 + sm) - (Number(breakMinutes) || 0)
  if (totalMin <= 0) return 0
  return Math.round((totalMin / 60) * 100) / 100
}

// ── Per-stop location details (sql/083, Lisa bug 2026-08-26) ────────────
// MoveDetailsEditor writes flat stop_N_* keys (sqft, levels, driveway,
// walk_distance, stairs, stairs_steps, elevator, apartment,
// apartment_floor) into the client/estimate form, mirroring the from_*/
// to_* columns. Stops are unbounded — and stop indices can grow past the
// visible count as stops are added/removed — so instead of N×9 real
// columns the flat keys fold into ONE `stop_details` jsonb on write and
// expand back to flat keys on read. Nothing above this layer knows the
// difference: forms keep reading/writing form.stop_2_driveway, and the
// rows the store caches always carry the flat keys.
const STOP_DETAIL_KEY_RE = /^stop_\d+_[a-z_]+$/
function foldStopDetails(row) {
  const out = { ...row }
  let found = null
  for (const k of Object.keys(out)) {
    if (STOP_DETAIL_KEY_RE.test(k)) {
      if (!found) found = {}
      found[k] = out[k]
      delete out[k]
    }
  }
  if (found) {
    const existing = (out.stop_details && typeof out.stop_details === 'object') ? out.stop_details : {}
    out.stop_details = { ...existing, ...found }
  }
  return out
}
function expandStopDetails(row) {
  if (!row || !row.stop_details || typeof row.stop_details !== 'object') return row
  // Flat keys can't collide with real columns (none start with stop_),
  // so spreading the jsonb under the row is always safe.
  return { ...row.stop_details, ...row }
}

// Storage v2 field normalizers (sql/086) — '' → null for dates/money.
const storageCleanDates = (obj, keys) => {
  keys.forEach(k => { if (obj[k] === '' || obj[k] === undefined) obj[k] = null })
  return obj
}
const storageCleanMoney = (obj, keys) => {
  keys.forEach(k => {
    if (obj[k] === '' || obj[k] === undefined) obj[k] = null
    else if (obj[k] !== null) obj[k] = parseFloat(obj[k]) || null
  })
  return obj
}

const db = {
  // CLIENTS
  async getClients() {
    const { data, error } = await supabase.from('clients').select('*').order('created_at', { ascending: false })
    if (error) throw error
    return (data || []).map(expandStopDetails)
  },

  async upsertClient(client) {
    // Clean empty strings for date columns — Supabase rejects "" for DATE type
    let cleaned = foldStopDetails({ ...client })
    ;['move_date', 'walkthrough_date'].forEach(k => { if (cleaned[k] === '' || cleaned[k] === undefined) cleaned[k] = null })
    // Moves (sql/094): the move fields on a client are a MIRROR of the
    // client's current move. An UPDATE never writes them — a stale full-row
    // save would drag the move backwards through the trigger. A brand-new
    // client keeps them: clients_ensure_move_trg seeds the lead move from
    // exactly these columns.
    if (cleaned.id) cleaned = stripKeys(cleaned, MOVE_MIRROR_CLIENT)
    delete cleaned.current_move_id
    const { data, error } = await supabase.from('clients').upsert(cleaned).select().single()
    if (error) throw error
    // A new client's current_move_id is stamped by an AFTER trigger — the
    // returned row predates it. Re-read so callers get the move id.
    if (!client.id && data?.id) {
      const { data: fresh } = await supabase.from('clients').select('*').eq('id', data.id).maybeSingle()
      if (fresh) return expandStopDetails(fresh)
    }
    return expandStopDetails(data)
  },

  // Targeted partial UPDATE of an existing client — unlike upsertClient (which
  // does an INSERT..ON CONFLICT and therefore needs NOT NULL columns like
  // `name`), this only touches the provided columns, so callers can safely
  // patch a couple of fields (e.g. walkthrough_status) without sending the
  // whole row (which risks clobbering fresher values with a stale copy).
  async patchClient(id, patch) {
    let cleaned = foldStopDetails({ ...patch })
    // Moves (sql/094): date / address / job-type keys belong to the client's
    // current move. Old call sites that still patch them on the client are
    // redirected: the move gets the change (its trigger mirrors it back),
    // and the client patch keeps only people-fields.
    const moveKeys = Object.keys(cleaned).filter(k => MOVE_MIRROR_CLIENT.includes(k) || /^stop_\d+_/.test(k))
    if (moveKeys.length) {
      const { data: row } = await supabase.from('clients').select('current_move_id').eq('id', id).maybeSingle()
      if (row?.current_move_id) {
        const mp = {}
        for (const k of moveKeys) {
          const v = cleaned[k]
          if (k === 'move_date') mp.load_date = v || null
          else if (k === 'move_time') mp.load_time = v || null
          else if (k === 'date_tentative') mp.load_tentative = !!v
          else if (k === 'pack_date') mp.pack_date = v || null
          else if (k === 'pack_time') mp.pack_time = v || null
          else if (k === 'walkthrough_date') mp.walkthrough_date = v || null
          else mp[k] = v
        }
        await this.upsertMove({ id: row.current_move_id, ...mp })
        cleaned = stripKeys(cleaned, moveKeys)
        if (Object.keys(cleaned).length === 0) {
          const { data: after } = await supabase.from('clients').select('*').eq('id', id).single()
          return expandStopDetails(after)
        }
      }
    }
    // A PARTIAL patch that includes stop_N_* keys must merge into the
    // row's existing stop_details, not replace it — otherwise patching
    // one stop's driveway would wipe every other stop's details.
    if (cleaned.stop_details) {
      try {
        const { data: cur } = await supabase.from('clients').select('stop_details').eq('id', id).maybeSingle()
        if (cur?.stop_details && typeof cur.stop_details === 'object') {
          cleaned.stop_details = { ...cur.stop_details, ...cleaned.stop_details }
        }
      } catch (e) { /* best effort — worst case the patch's own keys win */ }
    }
    ;['move_date', 'walkthrough_date', 'pack_date'].forEach(k => { if (cleaned[k] === '') cleaned[k] = null })
    // Bump updated_at — estimator Details patches weren't touching it, so
    // freshness-sorted surfaces never saw field edits (QA M4 OBS-10).
    cleaned.updated_at = new Date().toISOString()
    const { data, error } = await supabase.from('clients').update(cleaned).eq('id', id).select().single()
    if (error) throw error
    return expandStopDetails(data)
  },

  async deleteClient(id) {
    const { error } = await supabase.from('clients').delete().eq('id', id)
    if (error) throw error
  },

  // ── STORAGE v2 (sql/086, Jaylen 2026-09-08) ────────────────────────────
  // Three tables:
  //   storage_facilities — where (default = Extra Space; size price list)
  //   storage_units      — the ROOMS BBM rents (unit #, size, our cost,
  //                        rental vs internal). Fixed inventory.
  //   storage_tenancies  — WHO is in them (was storage_units in 084-085):
  //                        client link or free-text tenant_name, unit_ids[],
  //                        billed rate, paid_thru, fullness, in/out estimates.
  // RULE (carried from round 3): tenancy mutations go through patch*, never
  // a cached-row upsert — stale estimate links trip the FK.
  async getStorageFacilities() {
    const { data, error } = await supabase.from('storage_facilities').select('*').order('sort_order').order('name')
    if (error) { if (error.code === '42P01') return []; throw error }
    return data || []
  },
  async upsertStorageFacility(facility) {
    const cleaned = { ...facility, updated_at: new Date().toISOString() }
    if (!cleaned.id) delete cleaned.id
    if (cleaned.size_rates && typeof cleaned.size_rates === 'object') {
      const out = {}
      Object.entries(cleaned.size_rates).forEach(([k, v]) => { const n = parseFloat(v); if (k.trim() && !isNaN(n)) out[k.trim()] = n })
      cleaned.size_rates = out
    }
    const { data, error } = await supabase.from('storage_facilities').upsert(cleaned).select().single()
    if (error) throw error
    return data
  },
  async deleteStorageFacility(id) {
    const { error } = await supabase.from('storage_facilities').delete().eq('id', id)
    if (error) throw error
  },
  // Gate code — separate table, owner-tier RLS. Returns '' when the
  // caller can't see it (RLS → zero rows), so the UI can hide it quietly.
  async getStorageFacilityCode(facilityId) {
    const { data, error } = await supabase.from('storage_facility_codes').select('*').eq('facility_id', facilityId).maybeSingle()
    if (error) { if (error.code === '42P01') return null; throw error }
    return data
  },
  async saveStorageFacilityCode(facilityId, { gate_code = '', notes = '' }) {
    const { data, error } = await supabase.from('storage_facility_codes')
      .upsert({ facility_id: facilityId, gate_code, notes, updated_at: new Date().toISOString() })
      .select().single()
    if (error) throw error
    return data
  },

  async getStorageUnits() {
    const { data, error } = await supabase.from('storage_units').select('*').order('sort_order').order('unit_number')
    if (error) { if (error.code === '42P01') return []; throw error }
    return data || []
  },
  async upsertStorageUnit(unit) {
    const cleaned = storageCleanMoney({ ...unit, updated_at: new Date().toISOString() }, ['monthly_cost'])
    if (!cleaned.id) delete cleaned.id
    if (cleaned.unit_number != null) cleaned.unit_number = String(cleaned.unit_number).trim()
    // Numeric unit numbers sort naturally; text ones fall to the end.
    if (cleaned.sort_order == null) { const n = parseInt(cleaned.unit_number, 10); cleaned.sort_order = isNaN(n) ? 99999 : n }
    const { data, error } = await supabase.from('storage_units').upsert(cleaned).select().single()
    if (error) throw error
    return data
  },
  async patchStorageUnit(id, patch) {
    const cleaned = storageCleanMoney({ ...patch, updated_at: new Date().toISOString() }, ['monthly_cost'])
    const { data, error } = await supabase.from('storage_units').update(cleaned).eq('id', id).select().single()
    if (error) throw error
    return data
  },
  async deleteStorageUnit(id) {
    const { error } = await supabase.from('storage_units').delete().eq('id', id)
    if (error) throw error
  },

  async getStorageTenancies() {
    const { data, error } = await supabase.from('storage_tenancies').select('*').order('created_at', { ascending: false })
    if (error) { if (error.code === '42P01') return []; throw error }
    return data || []
  },
  // CREATE only — see the RULE above.
  async upsertStorageTenancy(t) {
    const cleaned = storageCleanMoney(storageCleanDates({ ...t }, ['moved_in_at', 'closed_at', 'paid_thru']), ['monthly_cost', 'monthly_rate'])
    if (!cleaned.client_id) cleaned.client_id = null
    if (!Array.isArray(cleaned.unit_ids)) cleaned.unit_ids = []
    cleaned.updated_at = new Date().toISOString()
    if (!cleaned.id) {
      delete cleaned.id
      try {
        const { data: { user } = {} } = await supabase.auth.getUser()
        if (user?.id) cleaned.created_by = user.id
      } catch (e) { /* non-fatal */ }
    }
    const { data, error } = await supabase.from('storage_tenancies').upsert(cleaned).select().single()
    if (error) throw error
    return data
  },
  async patchStorageTenancy(id, patch) {
    const cleaned = { ...patch }
    ;['moved_in_at', 'closed_at', 'paid_thru'].forEach(k => { if (cleaned[k] === '') cleaned[k] = null })
    ;['monthly_cost', 'monthly_rate'].forEach(k => {
      if (cleaned[k] === '') cleaned[k] = null
      else if (cleaned[k] !== undefined && cleaned[k] !== null) cleaned[k] = parseFloat(cleaned[k]) || null
    })
    if (cleaned.client_id === '') cleaned.client_id = null
    cleaned.updated_at = new Date().toISOString()
    const { data, error } = await supabase.from('storage_tenancies').update(cleaned).eq('id', id).select().single()
    if (error) throw error
    return data
  },
  async deleteStorageTenancy(id) {
    const { error } = await supabase.from('storage_tenancies').delete().eq('id', id)
    if (error) throw error
  },

  // CREW
  async getCrew() {
    const { data, error } = await supabase.from('crew_members').select('*').order('sort_order')
    if (error) throw error
    return data
  },

  async upsertCrew(member) {
    // New crew members append to the BOTTOM of the dispatch board (Jaylen
    // note 2026-07-08): the column default of 0 sorted every new hire to
    // the TOP of the crew column. Stamp max+1 on INSERT only, and never
    // override an explicit value — CrewReorderModal owns ordering after
    // that.
    const payload = { ...member }
    // System-linked auth/PIN columns are NEVER written through the
    // general upsert (2026-07-10 — Jensen's portal login broke): the
    // crew-invite flow re-links auth_user_id server-side, the desktop
    // store goes stale the moment that happens, and the next CrewModal
    // save silently reverted the link to the OLD auth account — locking
    // the crew member out of the portal. Same stale-store clobber class
    // as upsertEstimate's token strip (QA M2 BUG-5).
    ;['auth_user_id', 'pin_hash', 'pin_salt', 'pin_failed_attempts', 'pin_locked_until', 'pin_set_at']
      .forEach(k => { delete payload[k] })
    if (!payload.id && payload.sort_order == null) {
      const { data: maxRow } = await supabase.from('crew_members')
        .select('sort_order')
        .order('sort_order', { ascending: false, nullsFirst: false })
        .limit(1)
      payload.sort_order = ((maxRow?.[0]?.sort_order ?? 0) + 1)
    }
    const { data, error } = await supabase.from('crew_members').upsert(payload).select().single()
    if (error) throw error
    return data
  },

  async updateCrewSortOrder(id, sortOrder) {
    const { data, error } = await supabase.from('crew_members').update({ sort_order: sortOrder }).eq('id', id).select().single()
    if (error) throw error
    return data
  },

  async deleteCrew(id) {
    const { error } = await supabase.from('crew_members').delete().eq('id', id)
    if (error) throw error
  },

  // TRUCKS
  async getTrucks() {
    const { data, error } = await supabase.from('trucks').select('*').eq('active', true).order('number')
    if (error) throw error
    return data
  },

  async upsertTruck(truck) {
    const { data, error } = await supabase.from('trucks').upsert(truck).select().single()
    if (error) throw error
    return data
  },

  // TRUCK SERVICE LOG (sql/075_truck_service.sql)
  // Per-truck maintenance history + next-due tracking; trucks.tag_expiration
  // rides the normal upsertTruck path. Staff RLS all around.
  async getTruckServiceLog() {
    const { data, error } = await supabase
      .from('truck_service_log')
      .select('*')
      .order('service_date', { ascending: false })
      .order('created_at', { ascending: false })
    if (error) throw error
    return data || []
  },

  async addTruckServiceEntry(entry) {
    const { data: { user } = {} } = await supabase.auth.getUser()
    const { data, error } = await supabase
      .from('truck_service_log')
      .insert({ ...entry, created_by: user?.id || null })
      .select()
      .single()
    if (error) throw error
    return data
  },

  async deleteTruckServiceEntry(id) {
    if (!id) throw new Error('deleteTruckServiceEntry: id required')
    const { error } = await supabase.from('truck_service_log').delete().eq('id', id)
    if (error) throw error
  },

  // JOBS
  async getJobs(startDate, endDate) {
    let q = supabase.from('move_jobs').select('*').order('move_date').order('time')
    if (startDate) q = q.gte('move_date', startDate)
    if (endDate) q = q.lte('move_date', endDate)
    const { data, error } = await q
    if (error) throw error
    return data
  },

  async getAllJobs() {
    const { data, error } = await supabase.from('move_jobs').select('*').order('move_date').order('time')
    if (error) throw error
    return data
  },

  async upsertJob(job) {
    // Ghost holds (phaseKinds.js): a HOLD-only row never carries crew,
    // whichever path saved it — board drop, cascade, trip card, modal.
    const ghost = ghostHoldFields(job)
    const row = ghost ? { ...job, ...ghost } : job
    const { data, error } = await supabase.from('move_jobs').upsert(row).select().single()
    if (error) throw error
    return data
  },

  async deleteJob(id) {
    const { error } = await supabase.from('move_jobs').delete().eq('id', id)
    if (error) throw error
  },

  // CONFIG
  async getConfig() {
    const { data, error } = await supabase.from('app_config').select('*')
    if (error) throw error
    const config = {}
    ;(data || []).forEach(row => { config[row.key] = row.value })
    return config
  },

  async setConfig(key, value) {
    const { error } = await supabase.from('app_config').upsert({ key, value })
    if (error) throw error
  },

  // PHOTOS
  // Photos are returned in GALLERY order (oldest first) so the gallery
  // shows them in the same order they were captured during a walkthrough.
  // Order key is `sort_order` (sql/089, 2026-09-11): picker order stamped
  // at upload + manual reorder; uploaded_at is the tiebreak. (uploaded_at
  // alone was completion order — 3 concurrent uploads shuffled batches.)
  // Surfaces that want newest-first can sort client-side.
  async getPhotos(clientId) {
    const { data, error } = await supabase.from('photos').select('*').eq('client_id', clientId)
      .order('sort_order', { ascending: true, nullsFirst: false }).order('uploaded_at', { ascending: true })
    if (error) throw error
    return data || []
  },

  // Persist a manual gallery order: `orderedIds` is EVERY photo of the
  // client in the order they should display. Renumbers 1..n (later
  // uploads carry epoch-ms sort_orders, so they still land after these).
  // Small galleries, one UPDATE per row — fine; batch-of-one keeps RLS
  // simple and needs no RPC.
  async reorderPhotos(orderedIds) {
    const ids = (orderedIds || []).filter(Boolean)
    const results = await Promise.all(ids.map((id, i) =>
      supabase.from('photos').update({ sort_order: i + 1 }).eq('id', id)
    ))
    const failed = results.find(r => r.error)
    if (failed) throw failed.error
    return ids.length
  },

  async uploadPhoto(file, clientId, meta) {
    const ext = file.name.split('.').pop()
    const filePath = `${clientId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
    const { error: uploadErr } = await supabase.storage.from('photos').upload(filePath, file, { contentType: file.type })
    if (uploadErr) throw uploadErr
    const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(filePath)
    const row = { client_id: clientId, url: publicUrl, file_path: filePath, file_size: file.size, ...meta }
    // Include batch fields if provided
    if (meta.upload_batch_id) row.upload_batch_id = meta.upload_batch_id
    if (meta.batch_label !== undefined) row.batch_label = meta.batch_label
    // Picker order (sql/089): callers pass sort_order = batchStartMs +
    // index. Left undefined → the DB trigger stamps epoch ms of insert.
    if (meta.sort_order == null) delete row.sort_order
    const { data, error } = await supabase.from('photos').insert(row).select().single()
    if (error) throw error
    return data
  },

  // Replace the room_tags array on a photo. Used by the estimator's
  // post-upload tagging flow (Save Tags). Pass an empty array to clear.
  async updatePhotoTags(photoId, tags) {
    const safeTags = Array.isArray(tags) ? tags.filter(Boolean) : []
    const { data, error } = await supabase.from('photos')
      .update({ room_tags: safeTags })
      .eq('id', photoId)
      .select()
      .single()
    if (error) throw error
    return data
  },

  async deletePhoto(photo) {
    await supabase.storage.from('photos').remove([photo.file_path])
    const { error } = await supabase.from('photos').delete().eq('id', photo.id)
    if (error) throw error
  },

  // USER PROFILES
  // Store-wide roster read (feeds AssigneePicker co-assignee names).
  // Calls the security-definer FUNCTION staff_directory_list() (sql/078)
  // — id/user_id/display_name/role only, sorted by name — so office users
  // don't get the full roster with emails + permission_overrides (QA M7
  // BUG-4). Replaced the sql/072 staff_directory VIEW: definer views
  // trip the Supabase security advisor, and ours was granted to anon
  // (staff enumeration via the public key — 2026-08-07 CRITICAL finding).
  // sql/079 drops the old view once this build is live. Admin-only
  // surfaces that need the full row use getUserProfilesAdmin below.
  async getUserProfiles() {
    const { data, error } = await supabase.rpc('staff_directory_list')
    if (error) throw error
    return data || []
  },

  // Full user_profiles rows (email, permission_overrides, created_at) for the
  // Users & Roles admin surface. RLS restricts the underlying table to
  // self + admin-tier, so office callers only ever see their own row here.
  async getUserProfilesAdmin() {
    const { data, error } = await supabase.from('user_profiles').select('*').order('created_at', { ascending: true })
    if (error) throw error
    return data || []
  },

  async updateUserRole(profileId, role) {
    const { data, error } = await supabase.from('user_profiles').update({ role }).eq('id', profileId).select().single()
    if (error) throw error
    return data
  },

  async updateUserProfile(profileId, fields) {
    const { data, error } = await supabase.from('user_profiles').update(fields).eq('id', profileId).select().single()
    if (error) throw error
    return data
  },

  async deleteUserProfile(profileId) {
    const { error } = await supabase.from('user_profiles').delete().eq('id', profileId)
    if (error) throw error
  },

  // USER INVITES
  async getInvites() {
    const { data, error } = await supabase.from('user_invites').select('*').order('created_at', { ascending: false })
    if (error) {
      if (error.code === '42P01') return [] // table doesn't exist yet
      throw error
    }
    return data || []
  },

  async createInvite(invite) {
    // Upsert on email so re-invites don't fail on UNIQUE constraint
    const { data, error } = await supabase.from('user_invites')
      .upsert(invite, { onConflict: 'email' })
      .select().single()
    if (error) throw error
    return data
  },

  async deleteInvite(inviteId) {
    const { error } = await supabase.from('user_invites').delete().eq('id', inviteId)
    if (error) throw error
  },

  async deleteInviteByEmail(email) {
    const { error } = await supabase.from('user_invites').delete().eq('email', email.toLowerCase())
    if (error) throw error
  },

  async markInviteAccepted(email) {
    const { error } = await supabase.from('user_invites').update({ status: 'accepted', accepted_at: new Date().toISOString() }).eq('email', email.toLowerCase())
    if (error) console.warn('Could not mark invite accepted:', error.message)
  },

  async updateInviteToken(inviteId, fields) {
    const { error } = await supabase.from('user_invites').update(fields).eq('id', inviteId)
    if (error) throw error
  },

  // CREW AVAILABILITY
  async getAvailability(startDate, endDate) {
    let q = supabase.from('crew_availability').select('*')
    if (startDate) q = q.gte('date', startDate)
    if (endDate) q = q.lte('date', endDate)
    const { data, error } = await q
    if (error) throw error
    return data || []
  },

  async setAvailability(crewId, date, status, note) {
    if (!status) {
      const { error } = await supabase.from('crew_availability').delete().eq('crew_id', crewId).eq('date', date)
      if (error) throw error
      return null
    }
    const { data, error } = await supabase.from('crew_availability').upsert(
      { crew_id: crewId, date, status, note: note || '' },
      { onConflict: 'crew_id,date' }
    ).select().single()
    if (error) throw error
    return data
  },

  // CREW PORTAL TOKENS
  async generatePortalToken(crewId, validDate) {
    // Generate a random token
    const tokenBytes = new Uint8Array(16)
    crypto.getRandomValues(tokenBytes)
    const token = Array.from(tokenBytes).map(b => b.toString(16).padStart(2, '0')).join('')
    // Upsert (one token per crew per date)
    const { data, error } = await supabase.from('crew_portal_tokens').upsert(
      { crew_id: crewId, token, valid_date: validDate },
      { onConflict: 'crew_id,valid_date' }
    ).select().single()
    if (error) throw error
    return data.token
  },

  async getPortalToken(crewId, validDate) {
    const { data, error } = await supabase.from('crew_portal_tokens').select('token').eq('crew_id', crewId).eq('valid_date', validDate).maybeSingle()
    if (error) throw error
    return data?.token || null
  },

  // Admin: clear a crew lead's PIN so they can set a fresh one on next
  // visit to /crew. Calls the SQL helper crew_portal_reset_pin (defined
  // in sql/015_crew_portal_auth.sql) which also revokes any active
  // sessions for that crew member. Returns 'reset' on success.
  async resetCrewPortalPin(crewId) {
    const { data, error } = await supabase.rpc('crew_portal_reset_pin', { crew_uuid: crewId })
    if (error) throw error
    return data
  },

  // Admin: send a crew portal invite. The API creates (or links) a
  // Supabase auth account for the crew member, then TEXTS them a setup
  // link via RingCentral. Returns { success, sms_sent, setup_url, ... } —
  // setup_url doubles as a copy/paste fallback when SMS fails.
  // See api/crew-invite.js + crew-auth-plan-v1.md.
  async sendCrewPortalInvite(crewId, email) {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) throw new Error('Not signed in')
    const res = await fetch('/api/crew-invite', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ crew_member_id: crewId, email }),
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(body.error || `Server returned ${res.status}`)
    return body
  },

  // Pending/completed portal invites for one crew member (newest first).
  async getCrewInvites(crewId) {
    const { data, error } = await supabase.from('crew_invites')
      .select('*').eq('crew_id', crewId).order('created_at', { ascending: false })
    if (error) throw error
    return data
  },

  // Dispatch board snapshots
  async saveSnapshot(snapshotDate, jobsData, availabilityData) {
    const { data, error } = await supabase.from('dispatch_snapshots').upsert(
      { snapshot_date: snapshotDate, jobs_data: jobsData, availability_data: availabilityData },
      { onConflict: 'snapshot_date' }
    ).select().single()
    if (error) throw error
    return data
  },

  async getSnapshots(limit = 7) {
    const { data, error } = await supabase.from('dispatch_snapshots')
      .select('*')
      .order('snapshot_date', { ascending: false })
      .limit(limit)
    if (error) throw error
    return data || []
  },

  async getSnapshot(snapshotDate) {
    const { data, error } = await supabase.from('dispatch_snapshots')
      .select('*')
      .eq('snapshot_date', snapshotDate)
      .single()
    if (error) throw error
    return data
  },

  async deleteOldSnapshots(keepDays = 7) {
    const { format, addDays } = await import('date-fns')
    const cutoff = format(addDays(new Date(), -keepDays), 'yyyy-MM-dd')
    await supabase.from('dispatch_snapshots').delete().lt('snapshot_date', cutoff)
  },

  // ESTIMATES
  async getEstimates() {
    const { data, error } = await supabase.from('estimates').select('*').order('created_at', { ascending: false })
    if (error) { if (error.code === '42P01') return []; throw error }
    return (data || []).map(expandStopDetails)
  },

  async upsertEstimate(estimate) {
    // Fold flat stop_N_* location-detail keys into stop_details jsonb
    // (sql/083) — the Move Details editor on estimate rows writes the
    // same per-stop fields the client form does.
    let cleaned = foldStopDetails({ ...estimate })
    ;['move_date', 'walkthrough_date'].forEach(k => { if (cleaned[k] === '' || cleaned[k] === undefined) cleaned[k] = null })
    delete cleaned._move
    // ── Moves (sql/094): an estimate is an OPTION on a move. Its date /
    // address / job-type columns are a mirror the trigger maintains. Updates
    // never write them (a stale copy must not drag the move backwards);
    // inserts fill them from the move so the row is complete on first read.
    if (cleaned.id) {
      cleaned = stripKeys(cleaned, MOVE_MIRROR_ESTIMATE)
    } else if (cleaned.move_id) {
      const { data: mv } = await supabase.from('moves').select('*').eq('id', cleaned.move_id).maybeSingle()
      if (mv) cleaned = { ...cleaned, ...estimateFieldsFromMove(mv) }
    }
    // ── System-stamped columns are NEVER written through the general
    // upsert (QA M2 BUG-5 root cause). These fields are written directly
    // to the DB by their dedicated functions (generateWrittenEstimateLink,
    // deposit-link/transfer functions) or by server-side APIs (terms
    // signing, deposit webhooks, viewed_at stamps) — the store's copy of
    // the row goes stale the moment that happens, and the next full-row
    // save from any component was silently NULLing tokens, paid-deposit
    // records, and signed-terms stamps. Stripping them here makes the
    // general save path unable to clobber them.
    ;['written_estimate_token', 'written_estimate_sent_at', 'written_estimate_viewed_at',
      'deposit_token', 'deposit_amount', 'deposit_sent_at', 'deposit_paid_at',
      'deposit_transaction_id', 'deposit_receipt_url', 'deposit_last4', 'deposit_card_brand',
      'terms_token', 'terms_sent_at', 'terms_accepted_at', 'terms_version',
      'terms_ip', 'terms_user_agent', 'terms_source',
    ].forEach(k => { delete cleaned[k] })
    // Auto-set updated_at
    cleaned.updated_at = new Date().toISOString()
    // Remember whether this was an insert (no id yet) so we can apply
    // terms carry-forward + auto-seed phases after the write lands.
    const wasInsert = !cleaned.id

    // Stamp created_by on first insert. This is the key that lets the
    // RLS "creator visibility" rule work — the office user who builds
    // an estimate over the phone always sees it later, even if it's
    // never assigned (sql migration estimates_created_by). Don't
    // overwrite an existing value (already-saved estimates retain
    // their original creator on subsequent updates).
    if (wasInsert && cleaned.created_by === undefined) {
      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser()
        if (currentUser?.id) cleaned.created_by = currentUser.id
      } catch (e) {
        // Non-fatal — if we can't resolve the user the row just gets
        // created_by=NULL and only admins will see it (legacy behavior).
        console.warn('upsertEstimate: created_by stamp skipped —', e?.message || e)
      }
    }

    // Pre-read the existing row (if any) so we can detect status
    // transitions — notably non-booked → booked, which triggers an
    // automatic push of phases to the dispatch board.
    let priorStatus = null
    if (!wasInsert) {
      try {
        const { data: prior } = await supabase.from('estimates')
          .select('status').eq('id', cleaned.id).maybeSingle()
        priorStatus = prior?.status || null
      } catch (e) {
        // Non-fatal — we just won't auto-push on booking for this save.
        console.warn('upsertEstimate: prior-status read failed', e?.message || e)
      }
    }

    // (The old "can't book on a tentative date" gate is gone — moves-
    // architecture.md rule 4: booking is a commitment to the job, not the
    // calendar. Tentative dates ride along and get confirmed in the Book
    // confirm or the week of.)

    const { data, error } = await supabase.from('estimates').upsert(cleaned).select().single()
    if (error) throw error

    // Track the most up-to-date version of the row so we can chain writes
    // (carry-forward → phase seed) and return the fully-populated result.
    let current = data

    // Auto-seed a single 'move' phase on brand-new estimates that came in
    // with an empty planned_phases array. Mirrors the SQL backfill in
    // 020_phases.sql but runs for future inserts as well.
    if (wasInsert && current?.id) {
      const existing = Array.isArray(current.planned_phases) ? current.planned_phases : []
      if (existing.length === 0) {
        try {
          const seed = [makePhase({
            kind: 'move',
            sequence: 1,
            date: current.move_date || null,
            crew_size: current.crew_size_needed || 0,
            trucks_needed: current.trucks_needed || '',
          })]
          const { data: seeded, error: sErr } = await supabase.from('estimates')
            .update({ planned_phases: seed }).eq('id', current.id).select().single()
          if (sErr) throw sErr
          if (seeded) current = seeded
        } catch (e) {
          console.warn('phase auto-seed skipped:', e?.message || e)
        }
      }
    }

    // Auto-apply terms carry-forward on newly-created estimates only. This
    // is the "Fully automatic on estimate create" policy: if the client has
    // a valid client-level signature (within TERMS_CARRY_FORWARD_DAYS and
    // matching the current TERMS_VERSION), mirror it onto the new estimate
    // so it comes out of the gate pre-signed with terms_source='carried_forward'.
    if (wasInsert && current?.id && current?.client_id && !current?.terms_accepted_at) {
      try {
        const patched = await this.applyCarryForwardIfValid(current.id, current.client_id)
        if (patched) current = patched   // row with signature applied
      } catch (e) {
        // Carry-forward is best-effort — never block estimate creation on it.
        console.warn('terms carry-forward skipped:', e?.message || e)
      }
    }

    // Auto-push phases to dispatch on booking transition. This is the
    // "phases-as-chunks" handoff: as soon as an estimate flips to
    // 'booked', every planned phase is mirrored onto a move_jobs row
    // (dated for the 'move' phase, dateless for pack/load/unload) so
    // the dispatcher sees the unscheduled chunks in the pool instantly.
    // Best-effort — a push failure must not block the status change.
    //
    // IDEMPOTENT GUARD (Jaylen bug 2026-05-12): if move_jobs already
    // exist for this estimate, the dispatcher is already managing them
    // via the board. Re-firing pushPhasesToDispatch on a subsequent
    // non-booked→booked re-transition can create duplicates when
    // phase.move_job_ids has drifted out of sync with the actual rows
    // (the legacy fallback in pushPhasesToDispatch only adopts rows
    // matching the first-phase + phase_sequence<=1 conditions; rows
    // with higher phase_sequence get missed and a new row is inserted).
    // Skipping the auto-push when jobs already exist preserves the
    // dispatcher's state and prevents phantom unassigned duplicates.
    // First-time booking path is unaffected — count will be 0.
    if (!wasInsert && current?.id && current?.status === 'booked' && priorStatus !== 'booked') {
      try {
        const { count, error: cErr } = await supabase.from('move_jobs')
          .select('id', { count: 'exact', head: true })
          .eq('estimate_id', current.id)
        if (cErr) throw cErr
        if (!count || count === 0) {
          await this.pushPhasesToDispatch(current.id)
        } else {
          console.log(`upsertEstimate: ${count} move_jobs already exist for estimate ${current.id}, skipping auto-push to avoid duplicates`)
        }
      } catch (e) {
        console.warn('auto-push on booking skipped:', e?.message || e)
      }
    }
    // POSTPONED (2026-09-21): the job is on hold with no date. Legs that
    // aren't already running / done come off the board (deleted, not
    // cancelled — a cancelled row would still trip the count gate above
    // and block the re-push when they book again), and the move's dates
    // flip to tentative so the strip / chips / Dashboard read "~" until
    // someone confirms a new date. Quote, addresses and terms stay put.
    // Best-effort: a cleanup failure must not block the status change.
    if (!wasInsert && current?.id && current?.status === 'postponed' && priorStatus !== 'postponed') {
      try { await this.postponeEstimate(current) }
      catch (e) { console.warn('postpone cleanup skipped:', e?.message || e) }
    }
    return expandStopDetails(current)
  },

  // See the POSTPONED note in upsertEstimate. Returns { legsRemoved }.
  async postponeEstimate(est) {
    const { data: legs, error: lErr } = await supabase.from('move_jobs').select('id, status').eq('estimate_id', est.id)
    if (lErr) throw lErr
    const removable = (legs || []).filter(j => !['in_progress', 'completed'].includes(j.status)).map(j => j.id)
    if (removable.length) {
      const { error } = await supabase.from('move_jobs').delete().in('id', removable)
      if (error) throw error
    }
    if (est.move_id) {
      const { data: mv } = await supabase.from('moves').select('id, load_date, unload_date, pack_date').eq('id', est.move_id).maybeSingle()
      if (mv) {
        const patch = { updated_at: new Date().toISOString() }
        if (mv.load_date) patch.load_tentative = true
        if (mv.unload_date) patch.unload_tentative = true
        if (mv.pack_date) patch.pack_tentative = true
        const { error } = await supabase.from('moves').update(patch).eq('id', mv.id)
        if (error) throw error
      }
    }
    return { legsRemoved: removable.length }
  },

  // Change an estimate's move_date in place — and, optionally, shift its
  // already-booked dispatch jobs by the same number of days so the move
  // actually reschedules instead of forcing a rebuild. Multi-day phases keep
  // their relative spacing (every linked job moves by the same delta), and
  // the per-day `trip_tags` map is re-keyed to the new dates. Editing the
  // estimate date alone never re-triggers the phase push (that only fires on
  // a non-booked → booked transition), so this is safe on booked estimates.
  async shiftEstimateMoveDate(estimateId, newDate, { shiftJobs = false } = {}) {
    const nd = (newDate === '' || newDate === undefined) ? null : newDate
    // Moves (sql/094): the date lives on the move. Delegate; the mirror
    // writes the estimate row and callers get the same {estimate, shiftedJobs}.
    {
      const { data: e0 } = await supabase.from('estimates').select('id, move_id, move_date').eq('id', estimateId).maybeSingle()
      if (e0?.move_id) {
        const res = await this.shiftMoveDates(e0.move_id, nd, { shiftJobs, wholeTrip: shiftJobs })
        const { data: eAfter } = await supabase.from('estimates').select('*').eq('id', estimateId).single()
        return { estimate: expandStopDetails(eAfter), oldDate: e0.move_date || null, newDate: nd, shiftedJobs: res.shiftedJobs }
      }
    }
    const shiftStr = (dateStr, days) => {
      const d = new Date(dateStr + 'T12:00:00')
      d.setDate(d.getDate() + days)
      const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${dd}`
    }

    const { data: prior } = await supabase.from('estimates').select('id, move_date').eq('id', estimateId).maybeSingle()
    const oldDate = prior?.move_date || null

    const { data: updated, error } = await supabase.from('estimates')
      .update({ move_date: nd, updated_at: new Date().toISOString() })
      .eq('id', estimateId).select().single()
    if (error) throw error

    const shiftedJobs = []
    if (shiftJobs && oldDate && nd) {
      const delta = Math.round((new Date(nd + 'T12:00:00') - new Date(oldDate + 'T12:00:00')) / 86400000)
      if (delta !== 0) {
        const { data: jobs } = await supabase.from('move_jobs')
          .select('id, move_date, trip_tags').eq('estimate_id', estimateId)
        for (const j of (jobs || [])) {
          const patch = { updated_at: new Date().toISOString() }
          if (j.move_date) patch.move_date = shiftStr(j.move_date, delta)
          if (j.trip_tags && typeof j.trip_tags === 'object' && !Array.isArray(j.trip_tags)) {
            const re = {}
            for (const [k, v] of Object.entries(j.trip_tags)) {
              re[/^\d{4}-\d{2}-\d{2}$/.test(k) ? shiftStr(k, delta) : k] = v
            }
            patch.trip_tags = re
          }
          const { data: jUpdated, error: jErr } = await supabase.from('move_jobs')
            .update(patch).eq('id', j.id).select().single()
          if (!jErr && jUpdated) shiftedJobs.push(jUpdated)
        }
      }
    }
    return { estimate: updated, oldDate, newDate: nd, shiftedJobs }
  },

  // ===== Crew note sets (sql/060) ==========================================
  // Reusable blocks of crew-facing notes, owned by a client. A repeat client
  // accumulates one set per move (history is never lost); an estimate links
  // to one via estimates.crew_note_set_id. Because the estimate points at the
  // set rather than copying its text, editing from the estimate or the client
  // file edits the same row — always in sync — and the crew portal reads the
  // linked body live.
  async getCrewNoteSets(clientId) {
    if (!clientId) return []
    const { data, error } = await supabase.from('crew_note_sets')
      .select('*').eq('client_id', clientId).order('created_at', { ascending: false })
    if (error) { if (error.code === '42P01') return []; throw error }
    return data || []
  },

  async saveCrewNoteSet(set) {
    const cleaned = { ...set, updated_at: new Date().toISOString() }
    if (!cleaned.id) {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (user?.id && cleaned.created_by === undefined) cleaned.created_by = user.id
      } catch { /* non-fatal */ }
    }
    const { data, error } = await supabase.from('crew_note_sets').upsert(cleaned).select().single()
    if (error) throw error
    return data
  },

  async deleteCrewNoteSet(id) {
    // Detach from any estimates first so the FK (on delete set null) is clean
    // even if the column default changes later.
    await supabase.from('estimates').update({ crew_note_set_id: null }).eq('crew_note_set_id', id)
    const { error } = await supabase.from('crew_note_sets').delete().eq('id', id)
    if (error) throw error
    return true
  },

  // ── Internal (office-only) note sets (sql/063) ──────────────────────────
  // Mirror of crew note sets, but OFFICE-ONLY: never read by the crew portal,
  // never linked to estimates. A per-client, timestamped, editable list that
  // replaces the old single clients.internal_notes field.
  async getInternalNoteSets(clientId) {
    if (!clientId) return []
    const { data, error } = await supabase.from('internal_note_sets')
      .select('*').eq('client_id', clientId).order('created_at', { ascending: false })
    if (error) { if (error.code === '42P01') return []; throw error }
    return data || []
  },

  async saveInternalNoteSet(set) {
    const cleaned = { ...set, updated_at: new Date().toISOString() }
    if (!cleaned.id) {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (user?.id && cleaned.created_by === undefined) cleaned.created_by = user.id
      } catch { /* non-fatal */ }
    }
    const { data, error } = await supabase.from('internal_note_sets').upsert(cleaned).select().single()
    if (error) throw error
    return data
  },

  async deleteInternalNoteSet(id) {
    const { error } = await supabase.from('internal_note_sets').delete().eq('id', id)
    if (error) throw error
    return true
  },

  // ==========================================================================
  // TERMS CARRY-FORWARD
  // Given a brand-new estimate and its client, check whether the client has a
  // still-valid signature on file and, if so, copy it onto the estimate in
  // a single PATCH. Returns the patched estimate row on success, or null if
  // no valid carry-forward was available. Safe to call on any estimate —
  // it no-ops if terms are already accepted or the client has no signature.
  // ==========================================================================
  async applyCarryForwardIfValid(estimateId, clientId) {
    if (!estimateId || !clientId) return null

    // 1. Fetch the client's signature snapshot
    const { data: client, error: cErr } = await supabase.from('clients')
      .select('terms_signed_at, terms_signed_name, terms_version, terms_ip, terms_user_agent, terms_signed_via')
      .eq('id', clientId).maybeSingle()
    if (cErr) throw cErr
    if (!client || !client.terms_signed_at) return null

    // 2. Version gate — TERMS_VERSION bump invalidates every prior signature
    if (client.terms_version !== TERMS_VERSION) return null

    // 3. One-year window gate
    const signedAt = new Date(client.terms_signed_at).getTime()
    const ageDays = (Date.now() - signedAt) / 86400000
    if (!isFinite(ageDays) || ageDays > TERMS_CARRY_FORWARD_DAYS) return null

    // 4. Patch the estimate — no token generated (link was never sent);
    //    terms_source='carried_forward' distinguishes this from a direct sign
    //    in the audit trail.
    const patch = {
      terms_accepted_at: client.terms_signed_at,
      terms_version: client.terms_version,
      terms_ip: client.terms_ip,
      terms_user_agent: client.terms_user_agent,
      terms_source: 'carried_forward',
    }
    const { data: updated, error: uErr } = await supabase.from('estimates')
      .update(patch).eq('id', estimateId).select().single()
    if (uErr) throw uErr
    return updated
  },

  // Small helper so UI components can ask "does this client currently have
  // a valid carry-forward signature?" without duplicating the version /
  // window math. Returns { valid, signedAt, version, expiresAt } or null.
  async getClientTermsStatus(clientId) {
    if (!clientId) return null
    const { data: client, error } = await supabase.from('clients')
      .select('terms_signed_at, terms_signed_name, terms_version')
      .eq('id', clientId).maybeSingle()
    if (error) throw error
    if (!client || !client.terms_signed_at) return null
    const signedAt = new Date(client.terms_signed_at)
    const expiresAt = new Date(signedAt.getTime() + TERMS_CARRY_FORWARD_DAYS * 86400000)
    const versionMatches = client.terms_version === TERMS_VERSION
    const withinWindow = Date.now() <= expiresAt.getTime()
    return {
      valid: versionMatches && withinWindow,
      signedAt: client.terms_signed_at,
      signedName: client.terms_signed_name || null,
      version: client.terms_version || null,
      expiresAt: expiresAt.toISOString(),
      versionMatches,
      withinWindow,
    }
  },

  async deleteEstimate(id) {
    // move_jobs.estimate_id is not FK-cascaded, so we must delete the
    // estimate's pills ourselves first. Otherwise the dispatch board keeps
    // showing orphaned unassigned pills that can no longer be resolved to
    // any estimate. We also defensively null out estimate_id on any rows
    // that might remain (shouldn't happen, but covers cases where a pill
    // was manually converted to a standalone job).
    const { error: jobsError } = await supabase
      .from('move_jobs')
      .delete()
      .eq('estimate_id', id)
    if (jobsError) throw jobsError
    const { data: row } = await supabase.from('estimates').select('id, move_id').eq('id', id).maybeSingle()
    const { error } = await supabase.from('estimates').delete().eq('id', id)
    if (error) throw error
    // Deleting one option of several (2026-09-22): close the gap in the
    // letters so the move reads A, B — not A, C — unless a survivor has
    // already been sent as "Option C" (link / terms / deposit on it).
    // Returns the re-lettered siblings so the store can swap them in.
    if (row?.move_id) {
      try { return { relettered: await this.reletterOptions(row.move_id) } }
      catch (e) { console.warn('reletterOptions skipped:', e?.message || e) }
    }
    return { relettered: [] }
  },

  // A, B, C… by creation order for a move's options. No-op (returns [])
  // when the letters are already in order or when any option has been
  // sent to the client under its letter. Two passes because the unique
  // index is (job_number, option_letter): park on lowercase first.
  async reletterOptions(moveId) {
    const { data: opts, error } = await supabase.from('estimates')
      .select('id, option_letter, written_estimate_token, terms_token, deposit_paid_at, client_chosen_at')
      .eq('move_id', moveId).order('created_at')
    if (error) throw error
    if (!opts?.length) return []
    if (opts.some(o => o.written_estimate_token || o.terms_token || o.deposit_paid_at || o.client_chosen_at)) return []
    const target = opts.map((o, i) => ({ id: o.id, from: o.option_letter, to: String.fromCharCode(65 + i) }))
    const moves = target.filter(t => t.from !== t.to)
    if (!moves.length) return []
    for (const t of moves) {
      const { error: e1 } = await supabase.from('estimates').update({ option_letter: t.to.toLowerCase() }).eq('id', t.id)
      if (e1) throw e1
    }
    const out = []
    for (const t of moves) {
      const { data, error: e2 } = await supabase.from('estimates').update({ option_letter: t.to }).eq('id', t.id).select().single()
      if (e2) throw e2
      out.push(expandStopDetails(data))
    }
    return out
  },

  // ==========================================================================
  // PHASE PLANNER — working state on estimates.planned_phases (JSONB)
  // All helpers read the current array, mutate it in memory, then PATCH the
  // full array back. Callers receive the updated estimate row and should
  // replace their local copy with the returned value.
  // ==========================================================================

  // Fetch just the planner state for one estimate. Returns [] if the row
  // has no phases or is missing.
  async getPlannedPhases(estimateId) {
    if (!estimateId) return []
    const { data, error } = await supabase.from('estimates')
      .select('planned_phases').eq('id', estimateId).maybeSingle()
    if (error) throw error
    return Array.isArray(data?.planned_phases) ? data.planned_phases : []
  },

  // Replace the entire planner array in one write. Used internally by the
  // mutation helpers below and can also be called directly from UI that
  // wants to drag-reorder phases.
  async savePlannedPhases(estimateId, phases) {
    if (!estimateId) throw new Error('savePlannedPhases: estimateId required')
    const normalized = normalizePhases(phases)
    const { data, error } = await supabase.from('estimates')
      .update({ planned_phases: normalized, updated_at: new Date().toISOString() })
      .eq('id', estimateId).select().single()
    if (error) throw error
    return data
  },

  // Append a new phase to the end of the planner. Accepts the same options
  // as makePhase(). Returns the updated estimate row.
  async addPhase(estimateId, phaseFields = {}) {
    const current = await this.getPlannedPhases(estimateId)
    const next = [...current, makePhase({ ...phaseFields, sequence: current.length + 1 })]
    return this.savePlannedPhases(estimateId, next)
  },

  // Patch a single phase in place by its sequence number. fields is a
  // partial phase object; unknown keys are ignored by normalizePhases.
  async updatePhase(estimateId, sequence, fields) {
    const current = await this.getPlannedPhases(estimateId)
    const next = current.map(p => p.sequence === sequence ? { ...p, ...fields } : p)
    return this.savePlannedPhases(estimateId, next)
  },

  // Remove a phase by sequence and re-sequence the rest 1..N. If the phase
  // had a linked move_jobs row (move_job_id), delete that row too so the
  // dispatch board stays in sync.
  async removePhase(estimateId, sequence) {
    const current = await this.getPlannedPhases(estimateId)
    const target = current.find(p => p.sequence === sequence)
    if (target?.move_job_id) {
      try {
        await supabase.from('move_jobs').delete().eq('id', target.move_job_id)
      } catch (e) {
        console.warn('removePhase: failed to delete linked move_job', e?.message || e)
      }
    }
    const next = current.filter(p => p.sequence !== sequence)
    return this.savePlannedPhases(estimateId, next)
  },

  // Reorder phases by providing an array of sequence numbers in their new
  // order (e.g. [3, 1, 2] to move phase 3 to the front). Re-sequences 1..N.
  async reorderPhases(estimateId, newOrderSequences) {
    const current = await this.getPlannedPhases(estimateId)
    const bySeq = new Map(current.map(p => [p.sequence, p]))
    const reordered = newOrderSequences
      .map(seq => bySeq.get(seq))
      .filter(Boolean)
    // Append any phases the caller forgot about, to be safe
    current.forEach(p => { if (!newOrderSequences.includes(p.sequence)) reordered.push(p) })
    return this.savePlannedPhases(estimateId, reordered)
  },

  // ==========================================================================
  // PUSH PHASES TO DISPATCH
  // Mirror planned_phases onto move_jobs rows so each phase becomes its own
  // pill on the dispatch board. Spanning phases (duration_days > 1) expand
  // into N consecutive move_jobs rows — one per day, all sharing the same
  // phase_kind + phase_sequence. Idempotent: existing rows tracked via the
  // phase's move_job_ids array are updated in place; excess rows (from a
  // shrunk duration) are deleted.
  // ==========================================================================
  async pushPhasesToDispatch(estimateId) {
    if (!estimateId) throw new Error('pushPhasesToDispatch: estimateId required')

    // 1. Load the estimate with everything we'll need to mirror onto move_jobs
    const { data: est, error: eErr } = await supabase.from('estimates')
      .select('*').eq('id', estimateId).single()
    if (eErr) throw eErr
    if (!est) throw new Error('estimate not found')

    // Gate: a non-booked estimate has no business being on the dispatch
    // board. This catches both the manual PhasePlanner "Push to Dispatch"
    // button (which previously had no status check) AND any future code
    // paths that call this helper directly. The auto-push fired from
    // upsertEstimate is already inside a `status === 'booked'` branch,
    // so this throw never triggers there. The error message is what
    // the PhasePlanner toast surfaces back to the dispatcher.
    const mvRow = est.move_id ? await this.getMove(est.move_id).catch(() => null) : null
    if (est.status !== 'booked') {
      throw new Error(
        `Estimate must be in 'Booked' status before pushing to dispatch (currently '${est.status || 'building'}'). Change the estimate status first.`
      )
    }

    const phases = normalizePhases(est.planned_phases)
    if (phases.length === 0) return { created: 0, updated: 0, deleted: 0 }
    // Every kind must be storable BEFORE we touch move_jobs — a bad kind
    // used to fail the INSERT halfway through the loop and leave a
    // half-pushed trip (Margie Lee BBM-2609-021, 2026-09-15: the DB CHECK
    // rejected 'drive+unload+return'; sql/092 widened it). isValidKind is
    // the client-side mirror of that constraint.
    const bad = phases.find(p => !isValidKind(p.kind))
    if (bad) throw new Error(`Phase ${bad.sequence} has an unknown kind '${bad.kind}' — remove and re-add it in the planner.`)

    // 2. Load existing move_jobs for this estimate so we can match by id
    //    and reuse rows instead of creating duplicates.
    const { data: existingJobs, error: jErr } = await supabase.from('move_jobs')
      .select('*').eq('estimate_id', estimateId)
    if (jErr) throw jErr
    const jobById = new Map((existingJobs || []).map(j => [j.id, j]))

    // Track which existing job ids we've claimed, so anything left over can
    // be cleaned up (e.g. when a phase shrinks from 9 days to 3).
    const claimedIds = new Set()

    let created = 0
    let updated = 0
    const updatedPhases = []

    // Helper: YYYY-MM-DD + N days → YYYY-MM-DD (UTC, no tz drift)
    const addDays = (isoDate, days) => {
      if (!isoDate) return null
      const [y, m, d] = isoDate.split('-').map(Number)
      const dt = new Date(Date.UTC(y, m - 1, d))
      dt.setUTCDate(dt.getUTCDate() + days)
      return dt.toISOString().slice(0, 10)
    }

    for (const phase of phases) {
      // Shared fields mirrored from the estimate + phase working state.
      // IMPORTANT: these column names are move_jobs columns, NOT estimate
      // column names. The two tables use different naming conventions:
      //   estimates.job_type         → move_jobs.type
      //   estimates.origin_address   → move_jobs.from_address
      //   estimates.destination_address → move_jobs.to_address
      //   estimates.crew_size_needed → move_jobs.crew_size
      //   estimates.packing_needed   → move_jobs.packing
      // Fields with no move_jobs equivalent (trucks_needed, additional_stops,
      // job_number) are stashed into dispatcher_notes if the user filled them.
      // Build the trucks-text portion of dispatcher_notes from the
      // phase's primary truck AND the estimate's extra_trucks JSON.
      // Previously this only mentioned the primary, which caused the
      // expanded pill's "Truck Requirements" view to silently drop the
      // 16' from a "26' + 16'" estimate. The truck pills themselves
      // already read from estimates.extra_trucks (DispatchBoard.jsx
      // requirements badges), but this string was the source of the
      // visible mismatch in the Job Modal / pill expansion.
      const extraTrucksList = (() => {
        try {
          const arr = JSON.parse(est.extra_trucks || '[]');
          return Array.isArray(arr) ? arr.filter(Boolean) : [];
        } catch { return []; }
      })();
      const allTrucksLabel = [phase.trucks_needed, ...extraTrucksList].filter(Boolean).join(' + ');

      const notesParts = []
      if (phase.dispatcher_notes) notesParts.push(phase.dispatcher_notes)
      if (allTrucksLabel)         notesParts.push(`Trucks: ${allTrucksLabel}`)
      if (est.additional_stops)   notesParts.push(`Stops: ${est.additional_stops}`)

      // Duration on move_jobs is the estimated hours used by the
      // dispatch board, the crew portal "Est Xh" hint, and the legacy
      // invoice. Until this fix, pushPhasesToDispatch never set it, so
      // every newly-booked job inherited the DB default of 4 — even
      // when the estimate said 10. Now we mirror the right field per
      // phase kind so the data flows end-to-end.
      //   • move / load / unload — main labor leg, mirrors est.estimated_hours
      //   • pack                 — separate packing phase, mirrors est.packing_hours
      //   • drive / return / off — long-haul phases without a captured
      //                            hours field today; leave default 4
      //                            for now (TODO: per-phase hours UI)
      let phaseDuration = null;
      // Per-phase estimated hours (multi-day packing / multi-day pricing,
      // 2026-08-25): when the phase carries its own hours range, that WINS
      // over the estimate-level fallbacks below — each packing day lands
      // on the board with its own estimated duration.
      // trash_removal added 2026-08-06 (Quick Dispatch): dump runs carry
      // their estimated hours the same way the main labor legs do.
      // Fallback source derives from the kind's SEGMENTS (phaseKinds.js,
      // 2026-08-26 combo refactor): any labor segment (move/load/unload/
      // trash) → estimated_hours; a pack segment → packing_hours; pure
      // travel (drive/hold/return) → none.
      // 2026-09-10: a pack+labor COMBO day is BOTH — its board duration is
      // labor hours + packing hours (each from the phase when priced there,
      // else from the estimate), instead of packing_hours alone.
      const labor = phaseLaborPricing(phase);
      const pack = phasePackPricing(phase);
      const perPhaseLabor = labor.max || labor.min || 0;
      const perPhasePack = pack.max || pack.min || 0;
      const estLabor = Number(est.estimated_hours);
      const estPack = Number(est.packing_hours);
      let hours = 0;
      if (kindHasLabor(phase.kind)) {
        hours += perPhaseLabor > 0 ? perPhaseLabor : (Number.isFinite(estLabor) && estLabor > 0 ? estLabor : 0);
      }
      if (kindIsPack(phase.kind)) {
        hours += perPhasePack > 0 ? perPhasePack : (Number.isFinite(estPack) && estPack > 0 ? estPack : 0);
      }
      if (!kindHasLabor(phase.kind) && !kindIsPack(phase.kind)) {
        hours = perPhaseLabor; // drive/hold/return: only a hand-set per-day figure
      }
      if (hours > 0) phaseDuration = hours;

      const baseSharedFields = {
        client_id:      est.client_id,
        estimate_id:    est.id,
        type:           est.job_type || 'LOCAL',
        // A leg may carry its own stop (delivery / pickup / storage-out —
        // moves-architecture.md rule 7): phase.from_address / to_address
        // override the move's addresses on that leg only.
        from_address:   phase.from_address || est.origin_address || '',
        to_address:     phase.to_address || est.destination_address || '',
        from_address_override: phase.from_address || null,
        to_address_override:   phase.to_address || null,
        heavy_items:    est.heavy_items || '',
        packing:        est.packing_needed || '',
        time:           phase.time || est.move_time || '08:00',
        crew_size:      Number(phase.crew_size) || 0,
        // Only set duration when we have a confident value — otherwise
        // leave the existing column value (or DB default for inserts)
        // untouched so dispatcher edits in DispatchPillExpanded aren't
        // stomped on every re-push.
        ...(phaseDuration != null ? { duration: phaseDuration } : {}),
        dispatcher_notes: notesParts.join(' · '),
        phase_kind:     phase.kind,
        phase_sequence: phase.sequence,
        updated_at:     new Date().toISOString(),
      }

      // Dateless phases materialize as a single unscheduled job. Dated
      // phases expand into one row per day across duration_days.
      const duration = Math.max(1, Number(phase.duration_days) || 1)
      const spanCount = phase.date ? duration : 1

      const newIds = []
      const existingIds = Array.isArray(phase.move_job_ids) ? [...phase.move_job_ids] : []
      // Legacy carry-over: if move_job_ids is empty but move_job_id is set,
      // include it as the first slot so we don't orphan the old row.
      if (existingIds.length === 0 && phase.move_job_id) existingIds.push(phase.move_job_id)

      for (let dayOffset = 0; dayOffset < spanCount; dayOffset++) {
        // Dateless phases: no move_date so the row lands in Unassigned.
        // Dated phases: move_date = phase.date + dayOffset.
        // Anchor phases (MOVE for local, LOAD for BOL) inherit the
        // estimate's move_date so they appear on the correct day in the
        // unassigned row — all other phases stay dateless until the
        // dispatcher places the anchor on the board.
        // Pack phases inherit the estimate's pack_date (often the day before
        // the move) the same way the MOVE/LOAD anchor inherits move_date — so
        // packing lands on its own day on the board instead of sitting in
        // Unassigned until the dispatcher dates it. An explicit phase.date
        // (set in the planner or by a board drag) always wins.
        // Segment-derived (phaseKinds.js, 2026-08-26): any kind containing
        // a load/move segment anchors; any kind containing a pack segment
        // inherits pack_date. Pack wins below when a combo has both
        // (pack_load pre-dates the refactor with that behavior).
        const isAnchor = kindIsAnchor(phase.kind)
        const isPack = kindIsPack(phase.kind)
        // Moves (sql/094): an UNLOAD-side leg with no date of its own lands
        // on the move's unload date (a window → its first day). Everything
        // else follows the old rule (pack → pack_date, anchor → load date).
        const isUnloadLeg = !isAnchor && !isPack && kindSegments(phase.kind).includes('unload')
        const move_date = phase.date ? addDays(phase.date, dayOffset)
          : (isPack && est.pack_date ? est.pack_date
          : (isAnchor && !phase.date ? (est.move_date || null)
          : (isUnloadLeg && mvRow?.unload_date ? mvRow.unload_date : null)))

        // duration_days: a dateless multi-day phase (a 7-day HOLD) is ONE
        // spanning row — the board and capacity already span on this
        // column, and the anchor cascade spaces the next sibling by it.
        // Dated phases are expanded one row per day above, so each is 1.
        const sharedFields = { ...baseSharedFields, move_date, duration_days: phase.date ? 1 : duration }

        // Try to reuse an existing linked row at this day slot
        let targetRow = null
        const linkedId = existingIds[dayOffset]
        if (linkedId && jobById.has(linkedId)) {
          targetRow = jobById.get(linkedId)
        } else if (dayOffset === 0) {
          // Links can go missing (a planner save from stale state, a
          // snapshot restore, a push that failed mid-way). Before inserting
          // a DUPLICATE, adopt an unclaimed row of the same sequence + kind
          // — that's the row the dispatcher has already placed and crewed.
          // Oldest first: a duplicate from a failed push is always newer
          // than the row the dispatcher actually placed.
          targetRow = (existingJobs || [])
            .filter(j => !claimedIds.has(j.id) && j.phase_sequence === phase.sequence && j.phase_kind === phase.kind)
            .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')))[0] || null
        }
        if (!targetRow && phase.sequence === 1 && dayOffset === 0) {
          // Legacy fallback: adopt any pre-phases move_jobs row for the estimate
          targetRow = (existingJobs || []).find(j => !j.phase_sequence || j.phase_sequence === 1) || null
          if (targetRow && claimedIds.has(targetRow.id)) targetRow = null
        }

        if (targetRow) {
          // A row the dispatcher already dated keeps its date when the
          // planner has none to offer (phase.date null) — re-pushing an
          // estimate used to yank placed pills back into Unassigned.
          // Anchor / pack rows still follow the estimate's dates (the office
          // changing the move date must move the LOAD pill).
          const fields = { ...sharedFields }
          if (!phase.date && !isAnchor && !isPack && targetRow.move_date) delete fields.move_date
          // Ghost hold: a re-push strips any crew an older build left on
          // the hold row (it used to inherit the loader's crew).
          if (kindIsHoldOnly(phase.kind)) {
            Object.assign(fields, ghostHoldFields({ ...targetRow, ...fields, phase_kind: phase.kind }))
          }
          const { error: uErr } = await supabase.from('move_jobs')
            .update(fields).eq('id', targetRow.id)
          if (uErr) throw uErr
          updated++
          claimedIds.add(targetRow.id)
          newIds.push(targetRow.id)
        } else {
          const insertRow = { ...sharedFields, status: 'unassigned' }
          if (kindIsHoldOnly(phase.kind)) Object.assign(insertRow, ghostHoldFields({ ...insertRow, phase_kind: phase.kind }))
          const { data: inserted, error: iErr } = await supabase.from('move_jobs')
            .insert(insertRow)
            .select().single()
          if (iErr) throw iErr
          created++
          claimedIds.add(inserted.id)
          newIds.push(inserted.id)
        }
      }

      // If the phase shrank (duration went down), any previously-linked
      // rows beyond spanCount need to be deleted so they don't leak pills
      // onto the board.
      const orphanIds = existingIds.slice(spanCount).filter(id => id && jobById.has(id) && !claimedIds.has(id))
      // (Deletion happens below, after the loop, alongside other cleanup.)

      updatedPhases.push({
        ...phase,
        move_job_ids: newIds,
        move_job_id: newIds[0] || null,
        _orphanIds: orphanIds,  // temp field, stripped before save
      })
    }

    // 3. Delete any move_jobs rows that used to belong to this estimate's
    //    phases but are no longer claimed (phase was removed OR duration
    //    shrank). Only touch rows carrying a phase_sequence so we don't
    //    accidentally wipe legacy rows the planner never owned.
    let deleted = 0
    const allOrphanIds = []
    for (const p of updatedPhases) {
      if (Array.isArray(p._orphanIds)) allOrphanIds.push(...p._orphanIds)
      delete p._orphanIds
    }
    for (const job of (existingJobs || [])) {
      if (claimedIds.has(job.id)) continue
      if (!job.phase_sequence) continue // leave legacy non-phase rows alone
      allOrphanIds.push(job.id)
    }
    if (allOrphanIds.length > 0) {
      const uniq = Array.from(new Set(allOrphanIds))
      const { error: dErr } = await supabase.from('move_jobs').delete().in('id', uniq)
      if (dErr) throw dErr
      deleted = uniq.length
    }

    // 4. Write the updated planner state back (with fresh move_job_ids)
    await this.savePlannedPhases(estimateId, updatedPhases)

    return { created, updated, deleted }
  },

  // ==========================================================================
  // QUICK DISPATCH (2026-08-06, Jaylen) — small jobs (delivery / dump run /
  // labor only / pickup) skip the estimate FUNNEL but not the estimate ROW.
  // Every dollar in the system (Records, invoices, quoted-vs-actual, the
  // rate itself) anchors to an estimate — move_jobs has no rate column —
  // so a truly estimate-less job is a billing orphan that reads $0 forever.
  // This helper creates a minimal ALREADY-BOOKED estimate as the billing
  // anchor and pushes its single phase onto the dispatch board in one call.
  // Staff never see the estimate funnel; everything downstream (recaps,
  // auto-complete transition, Records revenue) works exactly like a real
  // move. Callers: QuickDispatchModal (intake auto-offer, client-file
  // button, dispatch NEW cards).
  // ==========================================================================
  async quickDispatch({ client_id, move_id, job_type, move_date, time, origin_address, destination_address, crew_size, trucks_needed, hourly_rate, estimated_hours, notes }) {
    if (!client_id) throw new Error('quickDispatch: client_id required')
    if (!move_date) throw new Error('Pick a date for the job')
    const rate = Number(hourly_rate) || 0
    const hours = Number(estimated_hours) || 0
    const total = rate > 0 && hours > 0 ? Math.round(rate * hours * 100) / 100 : null

    // DUMP_RUN → 'trash_removal', DELIVERY → 'delivery', PICKUP → 'pickup'
    // (leg kinds, phaseKinds.js); labor-only stays a 'move' day.
    const kind = job_type === 'DUMP_RUN' ? 'trash_removal' : job_type === 'DELIVERY' ? 'delivery' : job_type === 'PICKUP' ? 'pickup' : 'move'
    const job_number = await this.getNextJobNumber()
    const phase = {
      ...makePhase({
        kind,
        sequence: 1,
        date: move_date,
        time: time || null,
        crew_size: Number(crew_size) || 0,
        trucks_needed: trucks_needed || '',
        dispatcher_notes: notes || '',
      }),
      hourly_rate: rate,
      est_min_hours: hours,
      est_max_hours: hours,
    }

    // Moves (sql/094): a quick job is a MOVE with one booked option and one
    // leg. Attach to the client's current move when it is still an empty
    // lead (fresh client file — the intake just seeded it); otherwise make
    // a new move so an old job's data never bleeds into this one.
    let moveId = move_id || null
    if (!moveId) {
      const { data: cl } = await supabase.from('clients').select('current_move_id').eq('id', client_id).maybeSingle()
      let cur = cl?.current_move_id ? await this.getMove(cl.current_move_id) : null
      if (cur && cur.status === 'lead') {
        const { count } = await supabase.from('estimates').select('id', { count: 'exact', head: true }).eq('move_id', cur.id)
        if (count) cur = null
      } else cur = null
      const mvFields = { job_type: job_type || 'DELIVERY', load_date: move_date, load_time: time || null, load_tentative: false, origin_address: origin_address || '', destination_address: destination_address || '', move_number: job_number }
      if (cur) { const u = await this.upsertMove({ id: cur.id, ...mvFields }); moveId = u.id }
      else { const nm = await this.createMove(client_id, mvFields); moveId = nm.id }
    }

    const est = await this.upsertEstimate({
      client_id,
      move_id: moveId,
      job_type: job_type || 'DELIVERY',
      status: 'booked',
      move_date,
      move_time: time || '',
      origin_address: origin_address || '',
      destination_address: destination_address || '',
      crew_size_needed: Number(crew_size) || 0,
      trucks_needed: trucks_needed || '',
      hourly_rate: rate,
      estimated_hours: hours,
      min_hours: hours || null,
      max_hours: hours || null,
      total_estimate: total,
      total_min: total,
      total_max: total,
      planned_phases: [phase],
      job_number,
      internal_notes: 'Created via Quick Dispatch (small-job fast path — no estimate funnel).',
    })

    // The booking auto-push only fires on a non-booked → booked TRANSITION
    // during an update — a row INSERTED as booked never auto-pushes. Push
    // explicitly so the job lands in the unassigned pool right now.
    const pushed = await this.pushPhasesToDispatch(est.id)
    return { estimate: est, ...pushed }
  },

  // ── Transfer deposit + signed terms between estimates (sql/056) ─────
  // For when the move changed materially after money/signatures were
  // collected (e.g. destination change — house purchase fell through).
  // The operator creates the replacement estimate via the normal New
  // Estimate flow, then transfers the trail. Super-admin+ only
  // (transfer_deposits capability — enforce at the call sites).
  //
  // MOVES (never copies — revenue reports must not count a deposit
  // twice): the deposit_* group (incl. the client's deposit link token —
  // UNIQUE column, which is why the source is nulled FIRST) and the
  // terms_* signature group (transfers as-is per Jaylen 2026-06-09:
  // same document, re-signing would be redundant). Waivers follow.
  // written_estimate_* stays put — that token belongs to the OLD quote
  // document.
  //
  // Sequence is null-source → write-target so the UNIQUE deposit_token
  // never exists on two rows. If the second write fails the trail is
  // held in memory and the error message carries enough to restore by
  // hand (has never been observed; worst case is benign).
  async transferDepositTrail(fromEstimateId, toEstimateId) {
    if (!fromEstimateId || !toEstimateId || fromEstimateId === toEstimateId) {
      throw new Error('Pick a different estimate to transfer to')
    }
    const { data: from, error: fErr } = await supabase.from('estimates').select('*').eq('id', fromEstimateId).single()
    if (fErr) throw fErr
    const { data: to, error: tErr } = await supabase.from('estimates').select('id, job_number, client_id, internal_notes, deposit_paid_at').eq('id', toEstimateId).single()
    if (tErr) throw tErr
    if (from.client_id !== to.client_id) throw new Error('Both estimates must belong to the same client')
    if (!from.deposit_paid_at && !from.terms_accepted_at && !from.deposit_token) {
      throw new Error('Nothing to transfer — no deposit or signed terms on the source estimate')
    }
    if (to.deposit_paid_at) throw new Error(`${to.job_number} already has a paid deposit`)

    const trail = {
      deposit_token: from.deposit_token, deposit_amount: from.deposit_amount,
      deposit_sent_at: from.deposit_sent_at, deposit_paid_at: from.deposit_paid_at,
      deposit_transaction_id: from.deposit_transaction_id,
      deposit_receipt_url: from.deposit_receipt_url,
      deposit_last4: from.deposit_last4, deposit_card_brand: from.deposit_card_brand,
      terms_token: from.terms_token, terms_sent_at: from.terms_sent_at,
      terms_accepted_at: from.terms_accepted_at, terms_version: from.terms_version,
      terms_ip: from.terms_ip, terms_user_agent: from.terms_user_agent,
      terms_source: from.terms_source,
    }
    const stamp = new Date().toLocaleDateString('en-US')

    // 1) Clear the source (frees the UNIQUE tokens).
    const { error: clearErr } = await supabase.from('estimates').update({
      deposit_token: null, deposit_amount: null, deposit_sent_at: null,
      deposit_paid_at: null, deposit_transaction_id: null,
      deposit_receipt_url: null, deposit_last4: null, deposit_card_brand: null,
      terms_token: null, terms_sent_at: null, terms_accepted_at: null,
      terms_version: null, terms_ip: null, terms_user_agent: null, terms_source: null,
      internal_notes: `${from.internal_notes ? from.internal_notes + '\n' : ''}— Deposit + signed terms transferred to ${to.job_number} on ${stamp}.`,
    }).eq('id', from.id)
    if (clearErr) throw clearErr

    // 2) Land it on the target.
    const { data: updated, error: setErr } = await supabase.from('estimates').update({
      ...trail,
      internal_notes: `${to.internal_notes ? to.internal_notes + '\n' : ''}— Deposit + signed terms transferred in from ${from.job_number} on ${stamp}.`,
    }).eq('id', to.id).select().single()
    if (setErr) {
      throw new Error(`Transfer half-finished: cleared from ${from.job_number} but failed to land on ${to.job_number} (${setErr.message}). Deposit txn ${trail.deposit_transaction_id || 'n/a'}, amount ${trail.deposit_amount || 'n/a'} — re-run the transfer or restore manually.`)
    }

    // 3) Waivers follow the live estimate.
    const { error: wErr } = await supabase.from('waivers').update({ estimate_id: to.id }).eq('estimate_id', from.id)
    if (wErr) console.warn('transferDepositTrail: waiver re-point failed (finish manually):', wErr.message)

    return updated
  },

  // ── Merge duplicate clients (2026-07-02, tester feedback) ───────────
  // Everything attached to the duplicate (estimates, jobs, photos,
  // waivers, office + crew notes) re-points to the survivor; the
  // survivor's EMPTY fields fill in from the duplicate (never
  // overwrites); phone lists union by digits; then the duplicate row is
  // deleted. Super-admin gated at the call site. Not atomic — worst
  // case children moved but the dup row remains, which a re-run
  // finishes.
  async mergeClients(duplicateId, survivorId) {
    if (!duplicateId || !survivorId || duplicateId === survivorId) throw new Error('Pick a different client to merge into')
    const { data: dup, error: dErr } = await supabase.from('clients').select('*').eq('id', duplicateId).single()
    if (dErr) throw dErr
    const { data: surv, error: sErr } = await supabase.from('clients').select('*').eq('id', survivorId).single()
    if (sErr) throw sErr

    // Fill survivor's empty fields from the duplicate.
    const FILL = ['email', 'destination_address', 'additional_stops', 'move_date', 'lead_source', 'referral_name',
      'secondary_name', 'secondary_phone', 'secondary_email', 'heavy_items', 'access_notes', 'packing_notes']
    const patch = {}
    for (const k of FILL) {
      if ((surv[k] === null || surv[k] === '' || surv[k] === undefined) && dup[k]) patch[k] = dup[k]
    }
    // Union phones by digits; survivor's primary stays primary.
    const digits = (n) => String(n || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
    const sPhones = Array.isArray(surv.phones) ? [...surv.phones] : []
    const seen = new Set(sPhones.map(p => digits(p.number)))
    if (surv.phone && !seen.has(digits(surv.phone))) { sPhones.push({ number: digits(surv.phone), label: 'Mobile', is_primary: sPhones.length === 0 }); seen.add(digits(surv.phone)) }
    for (const p of (Array.isArray(dup.phones) ? dup.phones : [])) {
      const d = digits(p.number)
      if (d && !seen.has(d)) { sPhones.push({ ...p, is_primary: false }); seen.add(d) }
    }
    if (dup.phone && !seen.has(digits(dup.phone))) sPhones.push({ number: digits(dup.phone), label: 'Mobile', is_primary: false })
    if (sPhones.length && !sPhones.some(p => p.is_primary)) sPhones[0].is_primary = true
    if (sPhones.length) { patch.phones = sPhones; patch.phone = sPhones.find(p => p.is_primary)?.number || surv.phone }
    patch.internal_notes = `${surv.internal_notes ? surv.internal_notes + '\n' : ''}— Merged duplicate "${dup.name}" (${dup.id.slice(0, 8)}) into this client on ${new Date().toLocaleDateString('en-US')}.`

    if (Object.keys(patch).length) {
      const { error: pErr } = await supabase.from('clients').update(patch).eq('id', survivorId)
      if (pErr) throw pErr
    }

    // Re-point children.
    for (const table of ['estimates', 'move_jobs', 'photos', 'waivers', 'internal_note_sets', 'crew_note_sets']) {
      const { error } = await supabase.from(table).update({ client_id: survivorId }).eq('client_id', duplicateId)
      if (error && error.code !== '42P01') throw new Error(`Re-pointing ${table} failed: ${error.message}`)
    }

    // Delete the duplicate row.
    const { error: delErr } = await supabase.from('clients').delete().eq('id', duplicateId)
    if (delErr) throw delErr
    return { survivorId, moved: true }
  },

  // ===== MOVES (sql/094, moves-architecture.md) ===========================
  // The Date / Estimate / Job-type cluster. A move owns identity + dates;
  // estimates are options on it; move_jobs legs point at it. The DB mirrors
  // every move write onto the option rows and the client's current move
  // fields, so the surfaces that still read the old columns stay right.
  async getMoves() {
    const { data, error } = await supabase.from('moves').select('*').order('created_at', { ascending: false })
    if (error) { if (error.code === '42P01') return []; throw error }
    return data || []
  },

  async getMove(id) {
    const { data, error } = await supabase.from('moves').select('*').eq('id', id).maybeSingle()
    if (error) throw error
    return data
  },

  // Insert or partial-update a move. Blank dates → null; tentative flags
  // coerced to booleans; unload window cleared when unload is cleared.
  async upsertMove(move) {
    const cleaned = { ...move }
    ;['load_date', 'unload_date', 'unload_window_end', 'pack_date', 'walkthrough_date'].forEach(k => { if (cleaned[k] === '' || cleaned[k] === undefined) { if (k in cleaned) cleaned[k] = null } })
    ;['load_tentative', 'unload_tentative', 'pack_tentative'].forEach(k => { if (k in cleaned) cleaned[k] = !!cleaned[k] })
    if ('unload_date' in cleaned && !cleaned.unload_date) { cleaned.unload_window_end = null; cleaned.unload_time = null }
    if (cleaned.unload_window_end && cleaned.unload_date && cleaned.unload_window_end < cleaned.unload_date) cleaned.unload_window_end = cleaned.unload_date
    // flat stop_N_* keys → stop_details (same folding as clients/estimates)
    const folded = foldStopDetails(cleaned)
    ;['_client', '_options', '_legs'].forEach(k => { delete folded[k] })
    folded.updated_at = new Date().toISOString()
    let q
    if (folded.id) q = supabase.from('moves').update(folded).eq('id', folded.id)
    else q = supabase.from('moves').insert(folded)
    const { data, error } = await q.select().single()
    if (error) throw error
    return data
  },

  async deleteMove(id) {
    const { error } = await supabase.from('moves').delete().eq('id', id)
    if (error) throw error
  },

  // Remove a move the office added by mistake (Jaylen 2026-09-18: "+ New
  // move" had no way back). Only a BARE move goes — no options, no legs —
  // and only when the client keeps at least one other move; the newest
  // remaining move becomes current (re-firing the mirror so the client row
  // stops showing the deleted move's fields). Returns { client, currentId }.
  async removeMove(moveId) {
    const mv = await this.getMove(moveId)
    if (!mv) throw new Error('Move not found')
    const [{ count: nEst }, { count: nJobs }] = await Promise.all([
      supabase.from('estimates').select('id', { count: 'exact', head: true }).eq('move_id', moveId),
      supabase.from('move_jobs').select('id', { count: 'exact', head: true }).eq('move_id', moveId),
    ])
    if (nEst) throw new Error('This move has a quote on it — cancel or decline the quote first')
    if (nJobs) throw new Error('This move has jobs on the dispatch board — remove those first')
    const { data: others, error: oErr } = await supabase.from('moves').select('id, created_at').eq('client_id', mv.client_id).neq('id', moveId).order('created_at', { ascending: false })
    if (oErr) throw oErr
    if (!others?.length) throw new Error('A client keeps at least one move — edit this one instead')
    await this.deleteMove(moveId)
    const { data: cl } = await supabase.from('clients').select('id, current_move_id').eq('id', mv.client_id).maybeSingle()
    let client = cl
    if (!cl?.current_move_id) client = await this.setCurrentMove(mv.client_id, others[0].id)
    return { client, currentId: client?.current_move_id || others[0].id }
  },

  // ── Delete a client (Jaylen 2026-09-21: "no current way to delete a
  // client entry besides backend"). Owner / admin action behind a confirm
  // dialog. Refuses while anything real hangs off the client — a booked
  // or completed job on the board, a booked / completed / in-progress
  // quote, or a storage tenancy — those get cancelled first, on purpose,
  // in their own screens. Everything else goes: the DB cascades moves,
  // estimates, photo rows, note sets, document sends and notifications
  // (FKs on clients.id); jobs and waivers only SET NULL, so the client's
  // jobs are deleted here explicitly (an orphan leg on the board would be
  // worse than no leg) and the photos' storage objects are removed first
  // (the cascade can't reach the bucket). Returns { photos, jobs } counts.
  async deleteClient(clientId) {
    const [{ data: jobs, error: jErr }, { data: ests, error: eErr }, { data: tenancies, error: tErr }, { data: photos, error: pErr }] = await Promise.all([
      supabase.from('move_jobs').select('id, status').eq('client_id', clientId),
      supabase.from('estimates').select('id, status').eq('client_id', clientId),
      supabase.from('storage_tenancies').select('id').eq('client_id', clientId),
      supabase.from('photos').select('id, file_path').eq('client_id', clientId),
    ])
    if (jErr) throw jErr
    if (eErr) throw eErr
    if (tErr) throw tErr
    if (pErr) throw pErr
    // Unassigned / cancelled legs go with the client; anything crewed or
    // done is real history and blocks the delete.
    if ((jobs || []).some(j => ['assigned', 'dispatched', 'in_progress', 'completed'].includes(j.status)))
      throw new Error('This client has a job on the dispatch board that is assigned or done — cancel or remove it first')
    if ((ests || []).some(e => ['booked', 'completed'].includes(e.status)))
      throw new Error('This client has a booked or completed quote — cancel it on the Estimates page first')
    if ((tenancies || []).length)
      throw new Error('This client has storage on file — close the storage tenancy first')
    // Storage objects first: the row cascade would leave the files behind.
    const paths = (photos || []).map(p => p.file_path).filter(Boolean)
    if (paths.length) {
      const { error: sErr } = await supabase.storage.from('photos').remove(paths)
      if (sErr) console.warn('[deleteClient] photo storage cleanup failed', sErr.message)
    }
    if ((jobs || []).length) {
      const { error } = await supabase.from('move_jobs').delete().eq('client_id', clientId)
      if (error) throw error
    }
    // The client's current_move_id points at a move that is about to
    // cascade — clear it so the FK doesn't argue about order.
    const { error: cErr } = await supabase.from('clients').update({ current_move_id: null }).eq('id', clientId)
    if (cErr) throw cErr
    const { error } = await supabase.from('clients').delete().eq('id', clientId)
    if (error) throw error
    return { photos: paths.length, jobs: (jobs || []).length }
  },

  // New move for a client (lead). `fields` may carry identity + dates.
  // `fromMove` (a move object) pre-fills a follow-on move: the "Deliver
  // out of storage" case — origin = the previous destination, chained.
  async createMove(clientId, fields = {}, { fromMove = null, makeCurrent = true } = {}) {
    const seed = {}
    if (fromMove) {
      seed.origin_address = fromMove.destination_address || ''
      // Same job type as the move it follows — the delivery out of storage
      // is usually priced the same way the load-in was; the card's Edit
      // flips it when it isn't (E2E 2026-09-18: guessing LONG_DISTANCE
      // for a local load-in surprised more than it helped).
      seed.job_type = fromMove.job_type || null
      seed.follows_move_id = fromMove.id
      seed.storage_mode = fromMove.storage_mode === 'split' ? 'split' : seed.storage_mode
      seed.heavy_items = fromMove.heavy_items || ''
      seed.packing_needed = 'none'
    }
    // A blank new move starts Local (the common case) so the card and chip
    // don't read "Move · no date" with no type; the type flips on Edit.
    const mv = await this.upsertMove({ client_id: clientId, status: 'lead', job_type: 'LOCAL', packing_needed: 'tbd', ...seed, ...fields })
    if (makeCurrent) await this.setCurrentMove(clientId, mv.id)
    return mv
  },

  async setCurrentMove(clientId, moveId) {
    const { data, error } = await supabase.from('clients').update({ current_move_id: moveId, updated_at: new Date().toISOString() }).eq('id', clientId).select().single()
    if (error) throw error
    // re-fire the mirror so the client row shows the newly current move's fields
    await supabase.from('moves').update({ updated_at: new Date().toISOString() }).eq('id', moveId)
    return expandStopDetails(data)
  },

  // Append confirmations for the given date keys ('load' | 'unload' | 'pack')
  // and clear their tentative flags. `by` is a display name. Rule 5: a log,
  // not a lock — call it as often as the client re-confirms.
  async confirmMoveDates(moveId, keys, by) {
    const mv = await this.getMove(moveId)
    if (!mv) throw new Error('Move not found')
    const now = new Date().toISOString()
    const log = Array.isArray(mv.date_confirmations) ? [...mv.date_confirmations] : []
    const patch = { id: moveId, date_confirmations: log, dates_confirmed_at: now, dates_confirmed_by: by || null }
    for (const k of keys) {
      if (!['load', 'unload', 'pack'].includes(k)) continue
      const date = k === 'load' ? mv.load_date : k === 'unload' ? mv.unload_date : mv.pack_date
      log.push({ key: k, date, at: now, by: by || null })
      patch[`${k}_tentative`] = false
    }
    return this.upsertMove(patch)
  },

  // Mark a date tentative again (dates stay editable after booking).
  async setMoveTentative(moveId, key, tentative) {
    if (!['load', 'unload', 'pack'].includes(key)) throw new Error('bad date key')
    return this.upsertMove({ id: moveId, [`${key}_tentative`]: !!tentative })
  },

  // Slide the move's dates. `newLoadDate` sets load_date; with wholeTrip the
  // pack + unload (+ window end) move by the same delta; with shiftJobs every
  // linked dispatch row (and its trip_tags) slides too. Replaces the
  // estimate-level shiftEstimateMoveDate.
  async shiftMoveDates(moveId, newLoadDate, { shiftJobs = false, wholeTrip = true } = {}) {
    const mv = await this.getMove(moveId)
    if (!mv) throw new Error('Move not found')
    const nd = newLoadDate || null
    const delta = (mv.load_date && nd) ? daysBetween(mv.load_date, nd) : 0
    const patch = { id: moveId, load_date: nd }
    if (wholeTrip && delta) {
      if (mv.pack_date) patch.pack_date = addDaysYmd(mv.pack_date, delta)
      if (mv.unload_date) patch.unload_date = addDaysYmd(mv.unload_date, delta)
      if (mv.unload_window_end) patch.unload_window_end = addDaysYmd(mv.unload_window_end, delta)
    }
    const updated = await this.upsertMove(patch)
    const shiftedJobs = []
    if (shiftJobs && delta) {
      const { data: jobs } = await supabase.from('move_jobs').select('id, move_date, trip_tags').eq('move_id', moveId).neq('type', 'WALKTHROUGH')
      for (const j of (jobs || [])) {
        const jp = { updated_at: new Date().toISOString() }
        if (j.move_date) jp.move_date = addDaysYmd(j.move_date, delta)
        if (j.trip_tags && typeof j.trip_tags === 'object' && !Array.isArray(j.trip_tags)) {
          const re = {}
          for (const [k, v] of Object.entries(j.trip_tags)) re[/^\d{4}-\d{2}-\d{2}$/.test(k) ? addDaysYmd(k, delta) : k] = v
          jp.trip_tags = re
        }
        const { data: ju, error: jErr } = await supabase.from('move_jobs').update(jp).eq('id', j.id).select().single()
        if (!jErr && ju) shiftedJobs.push(ju)
      }
    }
    return { move: updated, delta, shiftedJobs }
  },

  // "Alternate pricing": a new option on an existing move. Copies the move's
  // identity into the row (the mirror keeps it there), shares the move number,
  // gets the next letter. `seed` = pricing fields from the pricing tab.
  async createOption(moveId, seed = {}) {
    const mv = await this.getMove(moveId)
    if (!mv) throw new Error('Move not found')
    const { data: sibs } = await supabase.from('estimates').select('id, job_number').eq('move_id', moveId).order('created_at')
    const jobNumber = mv.move_number || sibs?.[0]?.job_number || await this.getNextJobNumber()
    if (!mv.move_number) await this.upsertMove({ id: moveId, move_number: jobNumber })
    return this.upsertEstimate({ ...seed, client_id: mv.client_id, move_id: moveId, job_number: jobNumber, status: seed.status || 'building' })
  },

  // ── Day flags (full / closed) + truck downtime — the calendar gaps ──
  async getDayFlags() {
    const { data, error } = await supabase.from('day_flags').select('*').order('date')
    if (error) { if (error.code === '42P01') return []; throw error }
    return data || []
  },
  async setDayFlag(date, kind, note, by) {
    if (!kind) { const { error } = await supabase.from('day_flags').delete().eq('date', date); if (error) throw error; return null }
    const { data, error } = await supabase.from('day_flags').upsert({ date, kind, note: note || null, created_by: by || null }, { onConflict: 'date' }).select().single()
    if (error) throw error
    return data
  },
  async getTruckDowntime() {
    const { data, error } = await supabase.from('truck_downtime').select('*').order('from_date')
    if (error) { if (error.code === '42P01') return []; throw error }
    return data || []
  },
  async upsertTruckDowntime(row) {
    const { data, error } = await supabase.from('truck_downtime').upsert(row).select().single()
    if (error) throw error
    return data
  },
  async deleteTruckDowntime(id) {
    const { error } = await supabase.from('truck_downtime').delete().eq('id', id)
    if (error) throw error
  },

  async getNextJobNumber() {
    // Monotonic numbering via the next_job_number() counter function
    // (sql/070, QA M3 OBS-2): max+1 from existing rows re-issued numbers
    // whenever the month's highest-numbered estimates were deleted. The
    // DB counter survives deletions and serializes concurrent callers.
    try {
      const { data, error } = await supabase.rpc('next_job_number')
      if (!error && typeof data === 'string' && data.startsWith('BBM-')) return data
    } catch (e) { /* fall through to legacy */ }
    // Legacy fallback (pre-070 DBs / RPC failure): max+1 from rows.
    const { format } = await import('date-fns')
    const prefix = 'BBM-' + format(new Date(), 'yyMM') + '-'
    const { data } = await supabase.from('estimates').select('job_number').like('job_number', prefix + '%').order('job_number', { ascending: false }).limit(1)
    if (data && data.length > 0) {
      const lastNum = parseInt(data[0].job_number.split('-').pop()) || 0
      return prefix + String(lastNum + 1).padStart(3, '0')
    }
    return prefix + '001'
  },

  // ESTIMATE PHOTOS — photos linked to estimate instead of client
  async getEstimatePhotos(estimateId) {
    const { data, error } = await supabase.from('photos').select('*').eq('estimate_id', estimateId)
      .order('sort_order', { ascending: false, nullsFirst: false }).order('uploaded_at', { ascending: false })
    if (error) {
      // If estimate_id column doesn't exist yet, return empty
      if (error.message?.includes('estimate_id')) return []
      throw error
    }
    return data || []
  },

  async uploadEstimatePhoto(file, estimateId, meta) {
    const ext = file.name.split('.').pop()
    const filePath = `estimates/${estimateId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
    const { error: uploadErr } = await supabase.storage.from('photos').upload(filePath, file, { contentType: file.type })
    if (uploadErr) throw uploadErr
    const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(filePath)
    const row = { estimate_id: estimateId, url: publicUrl, file_path: filePath, file_size: file.size, ...meta }
    const { data, error } = await supabase.from('photos').insert(row).select().single()
    if (error) throw error
    return data
  },

  // DOCUMENT TEMPLATES — library of terms docs + waiver templates
  async getDocumentTemplates({ type, portal, includeInactive = false } = {}) {
    let q = supabase.from('document_templates').select('*').order('type').order('sort_order')
    if (!includeInactive) q = q.eq('is_active', true)
    if (type) q = q.eq('type', type)
    if (portal) q = q.contains('allowed_portals', [portal])
    const { data, error } = await q
    if (error) {
      if (error.code === '42P01') return [] // table doesn't exist yet
      throw error
    }
    return data || []
  },

  async createDocumentTemplate(template) {
    const { data, error } = await supabase.from('document_templates').insert(template).select().single()
    if (error) throw error
    return data
  },

  async updateDocumentTemplate(id, fields) {
    const payload = { ...fields, updated_at: new Date().toISOString() }
    const { data, error } = await supabase.from('document_templates').update(payload).eq('id', id).select().single()
    if (error) throw error
    return data
  },

  async deleteDocumentTemplate(id) {
    // Soft delete: flip is_active false so historical document_sends stay valid
    const { data, error } = await supabase.from('document_templates')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', id).select().single()
    if (error) throw error
    return data
  },

  // DOCUMENT SENDS — generated signing links + acceptance audit trail
  async getDocumentSends({ clientId, estimateId, jobId } = {}) {
    let q = supabase.from('document_sends')
      .select('*, template:document_templates(id, type, name, description)')
      .order('created_at', { ascending: false })
    if (clientId) q = q.eq('client_id', clientId)
    if (estimateId) q = q.eq('estimate_id', estimateId)
    if (jobId) q = q.eq('job_id', jobId)
    const { data, error } = await q
    if (error) {
      if (error.code === '42P01') return []
      throw error
    }
    return data || []
  },

  async createDocumentSend({ templateId, clientId, estimateId = null, jobId = null, sentBy = null, sentFromPortal = 'office' }) {
    // Generate a random URL-safe token
    const tokenBytes = new Uint8Array(16)
    crypto.getRandomValues(tokenBytes)
    const token = Array.from(tokenBytes).map(b => b.toString(16).padStart(2, '0')).join('')
    // Snapshot template version at time of send
    const { data: tmpl, error: tmplErr } = await supabase.from('document_templates')
      .select('current_version').eq('id', templateId).single()
    if (tmplErr) throw tmplErr
    const row = {
      template_id: templateId,
      client_id: clientId,
      estimate_id: estimateId,
      job_id: jobId,
      token,
      agreement_version: tmpl?.current_version || 1,
      sent_by: sentBy,
      sent_from_portal: sentFromPortal,
    }
    const { data, error } = await supabase.from('document_sends').insert(row).select().single()
    if (error) throw error
    return data
  },

  async markDocumentViewed(token) {
    // Only set viewed_at if not already set (first view wins)
    const { data, error } = await supabase.from('document_sends')
      .update({ viewed_at: new Date().toISOString() })
      .eq('token', token).is('viewed_at', null)
      .select().maybeSingle()
    if (error) throw error
    return data
  },

  async markDocumentAccepted(token, { docusignAgreementId = null, ip = null, userAgent = null } = {}) {
    const { data, error } = await supabase.from('document_sends')
      .update({
        accepted_at: new Date().toISOString(),
        docusign_agreement_id: docusignAgreementId,
        accepted_ip: ip,
        accepted_user_agent: userAgent,
      })
      .eq('token', token)
      .select().single()
    if (error) throw error
    return data
  },

  async getDocumentSendByToken(token) {
    const { data, error } = await supabase.from('document_sends')
      .select('*, template:document_templates(*), client:clients(id, name, email)')
      .eq('token', token).maybeSingle()
    if (error) throw error
    return data
  },

  // WRITTEN ESTIMATE LINKS — hosted client-facing ballpark estimate
  async generateWrittenEstimateLink(estimateId) {
    // Re-uses the existing token if one already exists (idempotent).
    const { data: existing, error: fetchErr } = await supabase.from('estimates')
      .select('written_estimate_token, written_estimate_sent_at')
      .eq('id', estimateId).single()
    if (fetchErr) throw fetchErr
    if (existing?.written_estimate_token) {
      // Backfill sent_at if a token exists without a stamp (QA M2 BUG-5 —
      // rows clobbered by the old stale-store upsert could hold a token
      // with no sent_at; the stamp is the NC written-estimate record).
      let sentAt = existing.written_estimate_sent_at
      if (!sentAt) {
        sentAt = new Date().toISOString()
        const { error: stampErr } = await supabase.from('estimates')
          .update({ written_estimate_sent_at: sentAt }).eq('id', estimateId)
        if (stampErr) throw stampErr
      }
      return { token: existing.written_estimate_token, alreadySent: true, sentAt }
    }
    // Generate new token + stamp sent_at
    const token = shortToken() // 12-char base62 — pretty /estimate/<code> links (publicLinks.js, 2026-09-10)
    const sentAt = new Date().toISOString()
    const { error: updateErr } = await supabase.from('estimates').update({
      written_estimate_token: token,
      written_estimate_sent_at: sentAt,
    }).eq('id', estimateId)
    if (updateErr) throw updateErr
    return { token, alreadySent: false, sentAt }
  },

  async markWrittenEstimateViewed(token) {
    // Only set viewed_at if not already set (first view wins)
    const { data, error } = await supabase.from('estimates')
      .update({ written_estimate_viewed_at: new Date().toISOString() })
      .eq('written_estimate_token', token)
      .is('written_estimate_viewed_at', null)
      .select().maybeSingle()
    if (error) throw error
    return data
  },

  async getEstimateByWrittenToken(token) {
    const { data, error } = await supabase.from('estimates')
      .select('*, client:clients(id, name, email, phone)')
      .eq('written_estimate_token', token).maybeSingle()
    if (error) throw error
    return data
  },

  // ==========================================================================
  // DEPOSIT LINKS (Square)
  // Separate token from written estimate token — staff may want to send a
  // deposit link without sending a written estimate, and vice versa.
  // ==========================================================================

  async getDepositPresets() {
    // Returns an array of preset amounts, e.g. [100, 250, 500, 1000]
    const { data, error } = await supabase.from('app_config')
      .select('value').eq('key', 'deposit_presets').maybeSingle()
    if (error) {
      if (error.code === '42P01') return [100, 250, 500, 1000]
      throw error
    }
    if (!data?.value) return [100, 250, 500, 1000]
    try {
      const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value
      return Array.isArray(parsed) ? parsed : [100, 250, 500, 1000]
    } catch {
      return [100, 250, 500, 1000]
    }
  },

  async generateDepositLink(estimateId, amount) {
    // Idempotent per-amount: if the estimate already has a deposit_token AND
    // the amount matches, return the existing token. If the amount changes,
    // rotate the token so an old link can't be paid for a new amount.
    const numAmount = Number(amount)
    if (!isFinite(numAmount) || numAmount <= 0) {
      throw new Error('Deposit amount must be a positive number')
    }
    const { data: existing, error: fetchErr } = await supabase.from('estimates')
      .select('deposit_token, deposit_amount, deposit_sent_at, deposit_paid_at')
      .eq('id', estimateId).single()
    if (fetchErr) throw fetchErr

    // If already paid, refuse to regenerate — operator should clear payment first
    if (existing?.deposit_paid_at) {
      return {
        token: existing.deposit_token,
        amount: Number(existing.deposit_amount || 0),
        alreadyPaid: true,
        paidAt: existing.deposit_paid_at,
      }
    }

    // Re-use existing token if amount is unchanged
    if (existing?.deposit_token && Number(existing.deposit_amount || 0) === numAmount) {
      return {
        token: existing.deposit_token,
        amount: numAmount,
        alreadySent: true,
        sentAt: existing.deposit_sent_at,
      }
    }

    // Generate new token
    const token = shortToken() // 12-char base62 — pretty /estimate/<code> links (publicLinks.js, 2026-09-10)
    const sentAt = new Date().toISOString()
    const { error: updateErr } = await supabase.from('estimates').update({
      deposit_token: token,
      deposit_amount: numAmount,
      deposit_sent_at: sentAt,
    }).eq('id', estimateId)
    if (updateErr) throw updateErr
    return { token, amount: numAmount, alreadySent: false, sentAt }
  },

  async clearDepositLink(estimateId) {
    // Wipe deposit link + payment fields so operator can regenerate
    const { error } = await supabase.from('estimates').update({
      deposit_token: null,
      deposit_amount: null,
      deposit_sent_at: null,
      deposit_paid_at: null,
      deposit_transaction_id: null,
      deposit_receipt_url: null,
      deposit_last4: null,
      deposit_card_brand: null,
    }).eq('id', estimateId)
    if (error) throw error
  },

  async getEstimateByDepositToken(token) {
    const { data, error } = await supabase.from('estimates')
      .select('*, client:clients(id, name, email, phone)')
      .eq('deposit_token', token).maybeSingle()
    if (error) throw error
    return data
  },

  // ==========================================================================
  // TERMS OF SERVICE (native clickwrap)
  // Independent from the written estimate token so staff can send terms on
  // their own. The version tag is stamped at send-time so old signatures stay
  // linked to the version of the terms the client actually agreed to.
  // ==========================================================================

  async generateTermsLink(estimateId, { version = TERMS_VERSION } = {}) {
    // Idempotent: reuse existing token if one is set and terms haven't been
    // accepted yet. If already accepted, refuse to regenerate (operator must
    // clear first). If no token yet, mint a new one and stamp sent_at.
    const { data: existing, error: fetchErr } = await supabase.from('estimates')
      .select('terms_token, terms_sent_at, terms_accepted_at, terms_version, terms_source')
      .eq('id', estimateId).single()
    if (fetchErr) throw fetchErr

    if (existing?.terms_accepted_at) {
      // Carry-forward signatures block new link generation (policy: disallow).
      // Operator must explicitly clear the terms audit trail first.
      if (existing.terms_source === 'carried_forward') {
        const err = new Error('This estimate already has a valid signature carried forward from the client. To require a fresh signature, clear the terms first.')
        err.code = 'CARRY_FORWARD_ACTIVE'
        throw err
      }
      return {
        token: existing.terms_token,
        sentAt: existing.terms_sent_at,
        acceptedAt: existing.terms_accepted_at,
        version: existing.terms_version,
        alreadyAccepted: true,
      }
    }

    if (existing?.terms_token) {
      return {
        token: existing.terms_token,
        sentAt: existing.terms_sent_at,
        version: existing.terms_version || version,
        alreadySent: true,
      }
    }

    // Generate new token
    const token = shortToken() // 12-char base62 — pretty /estimate/<code> links (publicLinks.js, 2026-09-10)
    const sentAt = new Date().toISOString()
    const { error: updateErr } = await supabase.from('estimates').update({
      terms_token: token,
      terms_sent_at: sentAt,
      terms_version: version,
    }).eq('id', estimateId)
    if (updateErr) throw updateErr
    return { token, sentAt, version, alreadySent: false }
  },

  async clearTermsLink(estimateId) {
    // Wipe the entire terms audit trail so the operator can regenerate.
    // Also clears terms_source so a future carry-forward can land cleanly.
    const { error } = await supabase.from('estimates').update({
      terms_token: null,
      terms_sent_at: null,
      terms_accepted_at: null,
      terms_ip: null,
      terms_user_agent: null,
      terms_version: null,
      terms_source: null,
    }).eq('id', estimateId)
    if (error) throw error
  },

  async getEstimateByTermsToken(token) {
    const { data, error } = await supabase.from('estimates')
      .select('*, client:clients(id, name, email, phone)')
      .eq('terms_token', token).maybeSingle()
    if (error) throw error
    return data
  },

  // ==========================================================================
  // WAIVER LIBRARY (native clickwrap, many-per-estimate)
  // Uses the `waivers` table — see sql/018_waivers.sql. Each row is one
  // waiver of one type attached to one estimate, with its own deep-link
  // token, version tag, and audit trail.
  // ==========================================================================

  // Static definitions for UI dropdowns (browser-safe — no require of _lib)
  listWaiverTypes() {
    return WAIVER_DEFS.slice()
  },

  async listWaiversForEstimate(estimateId) {
    const { data, error } = await supabase.from('waivers')
      .select('*')
      .eq('estimate_id', estimateId)
      .order('created_at', { ascending: true })
    if (error) {
      if (error.code === '42P01') return []   // table not created yet
      throw error
    }
    return data || []
  },

  async addWaiver(estimateId, clientId, waiverType, { version = WAIVER_VERSION, createdBy = null, notes = null } = {}) {
    if (!estimateId) throw new Error('addWaiver: estimateId required')
    if (!VALID_WAIVER_KEYS.has(waiverType)) {
      throw new Error(`Unknown waiver type: ${waiverType}`)
    }

    // Refuse to create a duplicate pending (unsigned) waiver of the same
    // type on the same estimate. If one exists, return it instead — makes
    // the UI idempotent.
    const { data: existing, error: existErr } = await supabase.from('waivers')
      .select('*')
      .eq('estimate_id', estimateId)
      .eq('waiver_type', waiverType)
      .is('signed_at', null)
      .maybeSingle()
    if (existErr && existErr.code !== 'PGRST116') throw existErr
    if (existing) {
      return { waiver: existing, reused: true }
    }

    // Generate fresh token
    const token = shortToken() // 12-char base62 — pretty /estimate/<code> links (publicLinks.js, 2026-09-10)

    const row = {
      estimate_id: estimateId,
      client_id: clientId || null,
      waiver_type: waiverType,
      waiver_version: version,
      token,
      sent_at: new Date().toISOString(),
      created_by: createdBy,
      notes,
    }
    const { data, error } = await supabase.from('waivers')
      .insert(row).select().single()
    if (error) throw error
    return { waiver: data, reused: false }
  },

  async clearWaiver(waiverId) {
    // Deletes the row entirely. The RLS policy blocks deleting signed
    // waivers from the client — attempts will fail with a policy error.
    const { error } = await supabase.from('waivers')
      .delete().eq('id', waiverId)
    if (error) throw error
  },

  async updateWaiverNotes(waiverId, notes) {
    const { error } = await supabase.from('waivers')
      .update({ notes }).eq('id', waiverId)
    if (error) throw error
  },

  async getWaiverByToken(token) {
    const { data, error } = await supabase.from('waivers')
      .select('*, estimate:estimates(id, job_number, move_date), client:clients(id, name, email, phone)')
      .eq('token', token).maybeSingle()
    if (error) throw error
    return data
  },

  // maybeMarkEstimateBooked — if BOTH deposit and terms are complete, flip
  // estimate status to 'booked'. Safe to call multiple times; no-op if the
  // estimate is already booked or one of the conditions isn't met.
  // Used by: Square payment success handler, DocuSign webhook (Step 4).
  async maybeMarkEstimateBooked(estimateId) {
    const { data: est, error } = await supabase.from('estimates')
      .select('id, status, deposit_paid_at, terms_accepted_at')
      .eq('id', estimateId).single()
    if (error) throw error
    if (!est) return { changed: false, reason: 'not_found' }
    if (est.status === 'booked') return { changed: false, reason: 'already_booked' }
    if (!est.deposit_paid_at) return { changed: false, reason: 'deposit_not_paid' }
    if (!est.terms_accepted_at) return { changed: false, reason: 'terms_not_signed' }
    const { error: updErr } = await supabase.from('estimates')
      .update({ status: 'booked' }).eq('id', estimateId)
    if (updErr) throw updErr
    return { changed: true }
  },

  // ==========================================================================
  // NOTIFICATIONS (in-app bell)
  // ==========================================================================

  async listNotifications({ unreadOnly = false, limit = 50 } = {}) {
    let q = supabase.from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit)
    if (unreadOnly) q = q.is('read_at', null)
    const { data, error } = await q
    if (error) {
      if (error.code === '42P01') return []
      throw error
    }
    return data || []
  },

  async getUnreadNotificationCount() {
    const { count, error } = await supabase.from('notifications')
      .select('id', { count: 'exact', head: true })
      .is('read_at', null)
    if (error) {
      if (error.code === '42P01') return 0
      return 0
    }
    return count || 0
  },

  async markNotificationRead(id) {
    const { error } = await supabase.from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id)
    if (error) throw error
  },

  async markAllNotificationsRead() {
    const { error } = await supabase.from('notifications')
      .update({ read_at: new Date().toISOString() })
      .is('read_at', null)
    if (error) throw error
  },

  async createNotification({ type, title, body = null, link = null, estimateId = null, clientId = null, userId = null }) {
    const { data, error } = await supabase.from('notifications').insert({
      user_id: userId,
      type, title, body, link,
      related_estimate_id: estimateId,
      related_client_id: clientId,
    }).select().single()
    if (error) throw error
    return data
  },

  // AUDIT LOG — per-record activity feed.
  //
  // Returns the audit_log rows for a specific (table_name, record_id),
  // newest-first. The ActivityLog component consumes this directly. The
  // table is RLS-protected so any authenticated office user can read all
  // rows; writes are blocked at the policy level (only the trigger fn
  // inserts via security definer).
  //
  // Returns: [{ id, occurred_at, user_id, user_email, user_name,
  //             actor_type, operation, changes }, ...]
  // where `changes` is JSON: {field: {from, to}} for updates, full row
  // snapshot for insert/delete.
  async getAuditLog(tableName, recordId, { limit = 100 } = {}) {
    const { data, error } = await supabase
      .from('audit_log')
      .select('id, occurred_at, user_id, user_email, user_name, actor_type, operation, changes')
      .eq('table_name', tableName)
      .eq('record_id', recordId)
      .order('occurred_at', { ascending: false })
      .limit(limit)
    if (error) throw error
    return data || []
  },

  // List query for the global Activity page (admin-only sales-tracking view).
  // All filter params are optional. Returns newest-first.
  //
  // Filters:
  //   - userId:    audit rows attributed to a specific user_id
  //   - tableName: 'clients' | 'estimates' | 'move_jobs'
  //   - operation: 'insert' | 'update' | 'delete'
  //   - sinceIso:  occurred_at >= this ISO timestamp (e.g., last 24h)
  //   - statusTo:  filter on changes.status.to (e.g., 'inactive' for lead drops)
  //   - limit:     max rows (defaults 200)
  async getAuditLogList({ userId, tableName, operation, sinceIso, statusTo, limit = 200 } = {}) {
    let query = supabase
      .from('audit_log')
      .select('id, occurred_at, user_id, user_email, user_name, actor_type, table_name, record_id, operation, changes')
      .order('occurred_at', { ascending: false })
      .limit(limit)

    if (userId) query = query.eq('user_id', userId)
    if (tableName) query = query.eq('table_name', tableName)
    if (operation) query = query.eq('operation', operation)
    if (sinceIso) query = query.gte('occurred_at', sinceIso)
    // JSONB path filter — match audit rows where `changes.status.to` equals
    // the given value. Used by the "Lead drops" / "New bookings" shortcuts.
    if (statusTo) query = query.eq('changes->status->>to', statusTo)

    const { data, error } = await query
    if (error) throw error
    return data || []
  },

  // RECAPS — daily-recap submissions surfaced from the move_jobs row
  //
  // Crew leads submit recaps via the crew portal (CrewPortalPage.jsx →
  // RecapForm). The fields land on the move_jobs row itself (actual_*
  // + customer_rating + recap_submitted_*). This helper fetches all
  // recapped jobs in a date range so the office-side HoursPage can
  // build per-crew rollups, and so the JobModal can show the recap
  // inline alongside its dispatch state.
  //
  // We join clients so callers can render the client name without an
  // additional fetch. crew_members aren't joined here because crew_ids
  // is an array — callers resolve names via the store's `crew` array.
  //
  // Filters (all optional):
  //   - since:  'YYYY-MM-DD' lower bound on move_date (inclusive)
  //   - until:  'YYYY-MM-DD' upper bound on move_date (inclusive)
  //   - crewId: only recaps where this crew member appears in crew_ids
  //   - limit:  defaults 500. BBM volume is small enough this works
  //             client-side; bump if you ever paginate.
  //
  // Windows key on move_date, NOT recap_submitted_at (QA M6 BUG-3):
  // pay periods are about when work HAPPENED. A Friday job recapped
  // Saturday morning must land in Friday's period, in the right OT
  // week, alongside its crew_day_hours row (also keyed by date).
  // Submission time remains display metadata.
  async getRecaps({ since, until, crewId, limit = 500 } = {}) {
    let q = supabase.from('move_jobs')
      .select(`
        id, client_id, type, move_date, time, status,
        crew_ids, leader_id, truck_ids,
        actual_start_time, actual_end_time, actual_hours, actual_lunch_minutes,
        customer_rating, crew_recap_notes, damage_notes,
        recap_submitted_at, recap_submitted_by,
        recap_photo_url, recap_photo_path,
        crew_pay_bonus_amount, crew_pay_bonus_notes,
        tip_total, tip_type, tip_splits,
        actual_hours_overrides,
        payroll_hours, payroll_start_time, payroll_end_time,
        payment_type, payment_amount,
        payment_check_photo_url, payment_check_photo_path,
        bol_photo_url, bol_photo_path,
        materials_used,
        packing_billing_mode,
        fuel_receipts,
        odometer_photo_url, odometer_photo_path, odometer_miles,
        phase_kind, phase_sequence,
        estimate_id,
        from_address, to_address,
        clients (id, name)
      `)
      .not('recap_submitted_at', 'is', null)
      .order('recap_submitted_at', { ascending: false })
      .limit(limit)
    if (since) q = q.gte('move_date', since)
    if (until) q = q.lte('move_date', until)
    if (crewId) q = q.contains('crew_ids', [crewId])
    const { data, error } = await q
    if (error) throw error
    return data || []
  },

  // PAYROLL ADJUSTMENTS — per-(crew_member, pay_period_start) row holding
  // hours adjustment + rate override + notes. Used by the Payroll page
  // to layer manual corrections on top of the recap-derived totals
  // without overwriting the underlying per-job recap data.
  //
  // periodStartIso is the Saturday date string 'YYYY-MM-DD' (from
  // src/lib/payPeriod.js → payPeriodFor(...).startIso).

  async getPayrollAdjustments({ periodStartIso } = {}) {
    if (!periodStartIso) return []
    const { data, error } = await supabase.from('payroll_adjustments')
      .select('*')
      .eq('period_start', periodStartIso)
    if (error) throw error
    return data || []
  },

  // Upsert a single adjustment. The unique constraint on
  // (crew_member_id, period_start) means a second call with the same
  // pair updates the existing row instead of creating a duplicate.
  async savePayrollAdjustment({ crewMemberId, periodStartIso, hoursAdjustment, rateOverride, notes }) {
    if (!crewMemberId || !periodStartIso) {
      throw new Error('savePayrollAdjustment: crewMemberId and periodStartIso are required')
    }
    const payload = {
      crew_member_id: crewMemberId,
      period_start: periodStartIso,
      hours_adjustment: Number(hoursAdjustment) || 0,
      rate_override: rateOverride === '' || rateOverride === null || rateOverride === undefined
        ? null
        : Number(rateOverride),
      notes: notes || null,
      updated_at: new Date().toISOString(),
    }
    const { data, error } = await supabase.from('payroll_adjustments')
      .upsert(payload, { onConflict: 'crew_member_id,period_start' })
      .select()
      .single()
    if (error) throw error
    return data
  },

  // CREW PAY BONUS — write the bonus amount + notes to the underlying
  // move_jobs row. The bonus implicitly belongs to whoever the leader_id
  // is at query time, so no separate crew_member_id column is needed.
  // Returns the updated row so callers can merge into local state.
  async saveCrewPayBonus(jobId, { amount, notes }) {
    if (!jobId) throw new Error('saveCrewPayBonus: jobId required')
    const payload = {
      crew_pay_bonus_amount: amount === '' || amount === null || amount === undefined
        ? null
        : Number(amount),
      crew_pay_bonus_notes: notes || null,
      updated_at: new Date().toISOString(),
    }
    const { data, error } = await supabase.from('move_jobs')
      .update(payload).eq('id', jobId)
      .select()
      .single()
    if (error) throw error
    return data
  },

  // MATERIALS CATALOG (sql/044_materials.sql)
  //
  // Admin-maintained list of materials BBM consumes on jobs. Cost is
  // admin-only by convention; the crew portal only renders name +
  // quantity picker. Soft-delete (active=false) so historical recap
  // snapshots in move_jobs.materials_used can still resolve their
  // material name when displayed.
  async listMaterialTypes({ includeInactive = false } = {}) {
    let q = supabase.from('material_types').select('*')
      .order('sort_order', { ascending: true })
      .order('name',       { ascending: true })
    if (!includeInactive) q = q.eq('active', true)
    const { data, error } = await q
    if (error) throw error
    return data || []
  },

  async saveMaterialType(row) {
    if (!row || !row.name) throw new Error('saveMaterialType: name required')
    const payload = {
      name:        String(row.name).trim(),
      unit:        row.unit || 'each',
      cost:        Number(row.cost)  || 0,
      price:       Number(row.price) || 0,
      sort_order:  row.sort_order != null ? Number(row.sort_order) : 100,
      active:      row.active !== false,
      updated_at:  new Date().toISOString(),
    }
    if (row.id) {
      const { data, error } = await supabase.from('material_types')
        .update(payload).eq('id', row.id).select().single()
      if (error) throw error
      return data
    }
    const { data, error } = await supabase.from('material_types')
      .insert(payload).select().single()
    if (error) throw error
    return data
  },

  // Soft-delete keeps historical recap snapshots resolvable. Real
  // deletes would orphan the {id: x, count: y} entries in move_jobs.
  async deleteMaterialType(id) {
    if (!id) throw new Error('deleteMaterialType: id required')
    const { error } = await supabase.from('material_types')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (error) throw error
    return true
  },

  // DAILY-DISPATCH GATE (sql/039_daily_dispatch_sent.sql)
  //
  // The dispatch board's push + lead-SMS pipeline is suppressed for
  // dates that haven't been "released" via the daily-dispatch button.
  // These helpers wrap the two SECURITY DEFINER functions that drive
  // that gating.
  //
  //   markDayDispatched(dateIso)
  //     Called from DailyDispatchModal after a successful bulk SMS
  //     send. Records the release AND clears any queued
  //     push_send_windows for jobs on that date (the SMS just covered
  //     them, so we don't want duplicate pushes 60 seconds later).
  //
  //   getPendingPushCountsByDate()
  //     Returns { 'YYYY-MM-DD': count } for the day-column badge on
  //     the dispatch board. Only dates that haven't been released yet
  //     show up — once a date is dispatched the count drops to 0 and
  //     the row falls out of this view.
  async markDayDispatched(dateIso, userId = null) {
    if (!dateIso) throw new Error('markDayDispatched: dateIso required')
    const { data, error } = await supabase.rpc('mark_day_dispatched', {
      dispatched_date: dateIso,
      actor_user_id: userId || null,
    })
    if (error) throw error
    return data
  },

  async getPendingPushCountsByDate() {
    const { data, error } = await supabase.rpc('pending_push_counts_by_date')
    if (error) throw error
    const out = {}
    for (const row of (data || [])) {
      // Postgres returns the date column as 'YYYY-MM-DD' from JSON
      out[row.move_date] = Number(row.pending_count) || 0
    }
    return out
  },

  // DISPATCH CHANGE LOG (sql/074_dispatch_change_log.sql)
  //
  // Office-originated edits to jobs on days whose Daily Dispatch SMS
  // already went out. A security-definer trigger writes the rows; the
  // SPA reads them for the amber "changes since send" chip + panel and
  // clears them (staff RLS UPDATE) when the dispatcher resends the day,
  // texts a single job's crew, or dismisses a job's group.
  //
  //   getDispatchChangeCounts()      → { 'YYYY-MM-DD': openCount } for
  //                                    today onward (drives the chip)
  //   getDispatchChanges(dateIso)    → open rows for one day, oldest first
  //   clearDispatchChanges(opts)     → sets cleared_* on open rows.
  //     opts: { date, jobId, reason } — date-wide (resend) or per-job
  //     (job_send / dismissed). At least one of date/jobId required.
  async getDispatchChangeCounts() {
    // Local (Eastern) date, not toISOString() — at 8pm ET the UTC date
    // is already tomorrow and the filter would hide today's changes.
    const d = new Date()
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const { data, error } = await supabase
      .from('dispatch_change_log')
      .select('move_date')
      .is('cleared_at', null)
      .gte('move_date', today)
    if (error) throw error
    const out = {}
    for (const row of (data || [])) {
      out[row.move_date] = (out[row.move_date] || 0) + 1
    }
    return out
  },

  async getDispatchChanges(dateIso) {
    if (!dateIso) throw new Error('getDispatchChanges: dateIso required')
    const { data, error } = await supabase
      .from('dispatch_change_log')
      .select('*')
      .eq('move_date', dateIso)
      .is('cleared_at', null)
      .order('changed_at', { ascending: true })
    if (error) throw error
    return data || []
  },

  async clearDispatchChanges({ date = null, jobId = null, reason }) {
    if (!date && !jobId) throw new Error('clearDispatchChanges: date or jobId required')
    if (!['resend', 'job_send', 'dismissed'].includes(reason)) {
      throw new Error(`clearDispatchChanges: invalid reason '${reason}'`)
    }
    const { data: { user } = {} } = await supabase.auth.getUser()
    let q = supabase
      .from('dispatch_change_log')
      .update({ cleared_at: new Date().toISOString(), cleared_by: user?.id || null, cleared_reason: reason })
      .is('cleared_at', null)
    if (date) q = q.eq('move_date', date)
    if (jobId) q = q.eq('job_id', jobId)
    const { error } = await q
    if (error) throw error
  },

  // PER-CREW-PER-JOB HOURS OVERRIDE
  //
  // Each move_jobs row carries actual_hours_overrides as a JSONB map:
  //   { "<crew_member_id>": <hours>, ... }
  // When a crew member has a key here, that's their effective hours on
  // this job (used for pay math + card/check tip splits). Missing key
  // means they inherit move_jobs.actual_hours along with the rest of the
  // crew.
  //
  // This helper does a read-modify-write so concurrent edits to OTHER
  // crew members' overrides don't stomp each other. Pass `hours = null`
  // or `hours = ''` to remove that crew member's override (they fall
  // back to actual_hours).
  async saveJobCrewHours(jobId, crewId, hours) {
    if (!jobId || !crewId) throw new Error('saveJobCrewHours: jobId + crewId required')
    // Fetch current overrides map so we can splice this one key in/out.
    const { data: existing, error: fetchErr } = await supabase
      .from('move_jobs')
      .select('actual_hours_overrides')
      .eq('id', jobId)
      .single()
    if (fetchErr) throw fetchErr
    const current = (existing?.actual_hours_overrides && typeof existing.actual_hours_overrides === 'object')
      ? { ...existing.actual_hours_overrides }
      : {}
    const numeric = hours === '' || hours === null || hours === undefined ? null : Number(hours)
    if (numeric === null || !Number.isFinite(numeric)) {
      delete current[crewId]
    } else {
      current[crewId] = numeric
    }
    const { data, error } = await supabase
      .from('move_jobs')
      .update({
        actual_hours_overrides: current,
        updated_at: new Date().toISOString(),
      })
      .eq('id', jobId)
      .select()
      .single()
    if (error) throw error
    return data
  },

  // ── Per-crew daily clock hours (sql/057) ───────────────────────────
  // The crew portal writes these via api/crew-portal-data (service key);
  // these helpers are the STAFF-side reads/edits for the Payroll page.
  //
  // getCrewDayHours pulls rows for a date window (inclusive). The Payroll
  // page joins them to recaps by (crew_member_id, work_date) so a day
  // with a row pays from computed_hours instead of the per-job chain.
  async getCrewDayHours({ since, until, crewIds } = {}) {
    let q = supabase.from('crew_day_hours').select('*')
    if (since) q = q.gte('work_date', since)
    if (until) q = q.lte('work_date', until)
    if (Array.isArray(crewIds) && crewIds.length > 0) q = q.in('crew_member_id', crewIds)
    const { data, error } = await q
    if (error) throw error
    return data || []
  },

  // Admin+ edit of a single day's paid hours from the Payroll page.
  // Read-modify-write so we don't clobber the stored clock_in/out or
  // break breakdown — we just set computed_hours to the corrected value.
  // Pass hours = null/'' to delete the row entirely (day reverts to the
  // per-job sql/052 fallback). recordedBy is the editing staff/crew id
  // when available (optional).
  async saveCrewDayHoursValue(crewMemberId, workDate, hours, recordedBy = null) {
    if (!crewMemberId || !workDate) throw new Error('saveCrewDayHoursValue: crewMemberId + workDate required')
    const numeric = hours === '' || hours === null || hours === undefined ? null : Number(hours)
    const { data: existingRows, error: fetchErr } = await supabase
      .from('crew_day_hours')
      .select('id')
      .eq('crew_member_id', crewMemberId)
      .eq('work_date', workDate)
      .limit(1)
    if (fetchErr) throw fetchErr
    const existing = existingRows && existingRows[0]

    // Blank → clear the daily override (revert to per-job fallback).
    if (numeric === null || !Number.isFinite(numeric) || numeric < 0) {
      if (existing) {
        const { error } = await supabase.from('crew_day_hours').delete().eq('id', existing.id)
        if (error) throw error
      }
      return null
    }

    if (existing) {
      const { data, error } = await supabase
        .from('crew_day_hours')
        .update({ computed_hours: numeric, updated_at: new Date().toISOString() })
        .eq('id', existing.id)
        .select()
        .single()
      if (error) throw error
      return data
    }
    const { data, error } = await supabase
      .from('crew_day_hours')
      .insert({
        crew_member_id: crewMemberId,
        work_date: workDate,
        computed_hours: numeric,
        recorded_by: recordedBy,
      })
      .select()
      .single()
    if (error) throw error
    return data
  },

  // Distinct list of actors who've made changes — used to populate the
  // user filter dropdown on the Activity page. Returns [{user_id,
  // user_name, user_email}, ...] de-duplicated.
  async getAuditLogActors() {
    const { data, error } = await supabase
      .from('audit_log')
      .select('user_id, user_name, user_email')
      .not('user_id', 'is', null)
      .order('user_name', { ascending: true })
      .limit(500)
    if (error) throw error
    const seen = new Set()
    const out = []
    for (const r of (data || [])) {
      if (seen.has(r.user_id)) continue
      seen.add(r.user_id)
      out.push(r)
    }
    return out
  },
}

export default db
